import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    HostListener,
    Inject,
    NgZone,
    ViewChild,
    ViewChildren,
    QueryList,
    ViewEncapsulation,
    OnInit,
    OnDestroy,
    inject,
    effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import {
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialogRef,
    MatDialog,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatMenuModule } from '@angular/material/menu';
import {
    MatPaginator,
    MatPaginatorModule,
    PageEvent,
} from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    NgxCsvParser,
    NgxCSVParserError,
    NgxCsvParserModule,
} from 'ngx-csv-parser';
import {
    catchError,
    finalize,
    firstValueFrom,
    forkJoin,
    from,
    of,
    switchMap,
    take,
    tap,
    throwError,
} from 'rxjs';
import { NoSpecialCharsDirective } from '../../no-special-chars.directive';
import { DisableWhileLoadingDirective } from 'app/modules/shared/button-loading.directive';
import { ButtonLoadingService } from 'app/modules/shared/button-loading.service';
import { LoadingSpinnerComponent } from 'app/core/components/loading-spinner/loading-spinner.component';
import { NoDataFoundComponent } from 'app/core/components/no-data-found/no-data-found.component';
import { SaiDialogHeaderComponent } from 'app/ui/sai-dialog-header/sai-dialog-header.component';
import {
    describeDistinctCount,
    readDistinctValuesTooltip,
    readIsUniqueFlag,
    readTableStatisticsScope,
    type TableStatisticsScope,
} from 'app/modules/shared/utils/table-structure-statistics-scope';
import { StructureScopeNoteComponent } from '../structure-scope-note/structure-scope-note.component';
import { CustomAlertService } from 'app/modules/shared/services/alert.service';
import { TableService } from '../../tables.service';
import { DataSourceService } from '../../dataSources.service';
import { StreamService } from 'app/company/tracker/new-tracker/stream.service';
import { UserService } from 'app/core/user/user.service';
import { KpiLogicService } from 'app/core/kpi-logic/kpi-logic.service';
import { ApiService } from '../../sse.service';
import {
    ExternalDatatype,
    externalDatatypeFromDialogType,
    isExternalTableDialogType,
} from '../../data-source-table-route.util';
import {
    agentWriteNeedsSave,
    // isAgentWriteTableDialog, // agent table writes are paused for now
    readAllowAgentWrite,
} from '../../table-agent-write.util';
import { TableHistoryComponent } from '../table-history/table-history.component';
import { MissingHeaderComponent } from './components/missing-header/missing-header.component';
import { environment } from 'environments/environment';
import { GuidlinesComponent } from '../tables/guidlines/guidlines.component';
import { Clipboard, ClipboardModule } from '@angular/cdk/clipboard';
import { CopyOnDblclickDirective } from '@fuse/directives/copy-on-dblclick.directive';
import { ColumnType, TableFileService } from './services/table-file.service';
import { TableUploadDoneInfo, TableUploadService } from './services/table-backend.service';
import {
    buildPickerGroups,
    buildTableSchemaPayload,
    SheetDraft,
    SheetDraftEdits,
    SheetDraftSet,
    sheetDraftTitle,
    draftsForChosenSheets,
    SheetPickerGroup,
    toTableNameSlug,
    UploadSourceFile,
    uploadFileKind,
    WorkbookSheet,
} from './services/workbook-sheets';
import { UploadFilesReaderService } from './services/upload-files-reader.service';
import {
    COLUMN_PROBLEM_LABEL,
    COLUMN_PROBLEM_TEXT,
    ColumnProblem,
    columnProblems,
    describeTableProblems,
    firstProblemColumn,
    problemCount,
    tableNameProblem,
    TableProblems,
} from './services/table-draft-validation';
import { WorkbookSheetPickerComponent } from './components/workbook-sheet-picker/workbook-sheet-picker.component';
import {
    FormulaPromptDialogComponent,
    FORMULA_DIALOG_TABLE_PREVIEW_MAX_ROWS,
} from './components/formula-prompt-dialog/formula-prompt-dialog.component';
import { AddKpiPromptDialogComponent } from './components/add-kpi-prompt-dialog/add-kpi-prompt-dialog.component';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { CoreLogicImportGuidelinesComponent } from './components/core-logic-import-guidelines/core-logic-import-guidelines.component';
import { TableStructureBackgroundService } from './services/table-structure-background.service';
import { AddTablesScrollChainDirective } from './add-tables-scroll-chain.directive';

interface GrainDisplayRow {
    label: string;
    value: string;
    nested?: boolean;
}

/** Column headers and one value row for `grain.temporal_coverage` in the grain panel. */
interface GrainTemporalCoverageColumn {
    label: string;
    value: string;
}

interface UploadPayload {
    name: string;
    description: string;
    dataSourceId?: string;
    table: Record<string, ColumnType>;
    comments: Record<string, string>;
    dateColumns: Record<string, string>;
    data?: any[];
    /** Explicit intent for the backend: true appends to an existing table, false creates a new one. */
    append?: boolean;
}

interface KpiLogicItem {
    id?: number | string;
    level?: number | string;
    kpi_name: string;
    calculation: string;
    columns_used?: string[];
    description: string;
    /** From API `isLocked`; request body uses `locked`. */
    isLocked?: boolean;
}

interface ColumnDataQualityFlagEntry {
    columnName: string;
    flags: string[];
}

/** Table upload / view / append dialog (MatDialog). */
@Component({
    selector: 'app-add-tables',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './add-tables.component.html',
    styleUrls: ['./add-tables.component.scss'],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TranslocoModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatButtonModule,
        MatSelectModule,
        MatOptionModule,
        MatCheckboxModule,
        MatMenuModule,
        MatTableModule,
        MatPaginatorModule,
        MatTooltipModule,
        MatProgressBarModule,
        MatProgressSpinnerModule,
        NoSpecialCharsDirective,
        DisableWhileLoadingDirective,
        NgxCsvParserModule,
        CopyOnDblclickDirective,
        ClipboardModule,
        AddTablesScrollChainDirective,
        LoadingSpinnerComponent,
        NoDataFoundComponent,
        SaiDialogHeaderComponent,
        StructureScopeNoteComponent,
        WorkbookSheetPickerComponent,
    ],
})
export class AddTablesComponent implements OnInit, OnDestroy {
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChildren('headerSpan') headerSpans!: QueryList<ElementRef<HTMLSpanElement>>;
    @ViewChildren('structureDescSpan')
    structureDescSpans!: QueryList<ElementRef<HTMLSpanElement>>;
    @ViewChild('appendFileInput') appendFileInput!: ElementRef<HTMLInputElement>;
    @ViewChild('coreLogicFileInput')
    coreLogicFileInput!: ElementRef<HTMLInputElement>;

    lang = 'en';
    userRole = '';
    loading = false;
    loadingOnSubmit = false;
    globalLoading = false;
    showProgress = false;
    isFinalizing = false;
    private _isTableUploadActive = false;
    /** While the upload overlay shows, the dialog cannot be closed (backdrop, Escape, close button). */
    get isTableUploadActive(): boolean {
        return this._isTableUploadActive;
    }
    set isTableUploadActive(value: boolean) {
        this._isTableUploadActive = value;
        this.syncUploadLock();
    }
    /** Held for a whole multi-sheet run, so the dialog stays locked between one sheet and the next. */
    private sheetBatchRunning = false;

    get isUploadLocked(): boolean {
        return this._isTableUploadActive || this.sheetBatchRunning;
    }

    private syncUploadLock(): void {
        if (this.dialogRef) {
            this.dialogRef.disableClose = this.isUploadLocked;
        }
    }

    /** Leaving or reloading the page mid-upload asks first. */
    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: BeforeUnloadEvent): void {
        if (this.isUploadLocked) {
            event.preventDefault();
            event.returnValue = '';
        }
    }

    /** New table: problems are highlighted once the user has tried to save. */
    showValidation = false;
    /** Problems per column of the table on screen (new table only). */
    columnProblemList: ColumnProblem[][] = [];
    /** Problem count per sheet draft key, shown on its tab once validation has run. */
    readonly draftProblemCounts = new Map<string, number>();
    progressValue = 0;
    uploadStatus = '';
    isAppending = false;
    isAppendFileLoading = false;

    structureViewMode: 'text' | 'table' = 'table';
    parsedStructure: any[] = [];
    structureTextViewValue = '';
    structureColumnKeys: string[] = [];
    hasUniqueColumn = false;
    private structureJsonRoot: Record<string, unknown> | null = null;
    /** Null when the served structure carried no marker, which means whole-table figures. */
    statisticsScope: TableStatisticsScope | null = null;
    structureDescEditStates: boolean[] = [];
    /** Key/value rows derived from `grain` in structure JSON or `tableInfo.grain`. */
    parsedGrainRows: GrainDisplayRow[] = [];
    /** Human-friendly table type label shown as a tag in Details header. */
    tableTypeDisplay = '';
    /** Populated when `grain.temporal_coverage` is a non-empty object. */
    grainTemporalCoverageColumns: GrainTemporalCoverageColumn[] = [];
    /** Populated from `table_shape` (rows/columns) when available. */
    tableShapeColumns: GrainTemporalCoverageColumn[] = [];
    savingExternalMeta = false;

    private initialExternalDescription = '';
    private initialExternalStructure = '';
    private savedExternalMetaThisVisit = false;

    headers: string[] = [];
    rows: any[][] = [];
    allRows: any[][] = [];
    types: ColumnType[] = [];
    editStates: boolean[] = [];
    metricDefinitions: KpiLogicItem[] = [];
    isRegeneratingKpis = false;
    isDeletingKpi = false;
    isSavingImportedCoreLogic = false;
    coreLogicImportedPendingSave = false;
    importedCoreLogicFileName = '';
    private importedCoreLogicFile: File | null = null;
    /** KPI id currently sending set-kpi-lock (string for stable compare). */
    lockingKpiId: string | null = null;
    /** Column name currently sending set-column-lock. */
    lockingStructureColumnName: string | null = null;
    /** Names of locked columns shown in the UI (includes unsaved toggles). */
    lockedStructureColumns = new Set<string>();
    /** Locked columns from the server when the dialog opened or last saved. */
    private initialLockedStructureColumns = new Set<string>();
    /** Saved "Allow agents to insert and update table data" value, as last loaded or saved. */
    private initialAllowAgentWrite = false;
    /** Column names to lock via API after a successful table save. */
    private pendingLockStructureColumns = new Set<string>();
    /** Server-locked column names to unlock via API after a successful table save. */
    private pendingUnlockStructureColumns = new Set<string>();
    private metricDefinitionsBeforeImport: KpiLogicItem[] | null = null;

    private originalHeaders: string[] = [];
    private originalHeadersClean: string[] = [];
    private originalTypes: ColumnType[] = [];
    private originalUnits: (string | null)[] = [];

    private apiSchema: Record<string, string> = {};
    private apiComments: Record<string, string> = {};
    /** Authoritative column comments from `get-table-meta` (date formats, units). */
    private metaApiComments: Record<string, string> = {};

    pageSize = 100;
    pageIndex = 0;
    length = 0;

    form!: FormGroup;
    unitOptions = [
        { name: 'Weight (kg)', value: 'Weight (kg)' },
        { name: 'Boxes', value: 'Boxes' },
        { name: 'Pieces', value: 'Pieces' },
        { name: 'Percentage', value: 'Percentage' },
        { name: 'Identifier (ID)', value: 'Identifier (ID)' },
        { name: 'No Unit', value: 'No Unit' },
    ];

    /** Multi-sheet workbook: every sheet the worker read, listed until the user picks. */
    /** Multi-sheet workbook or multi-file upload: what the picker lists, one group per file. */
    pickerGroups: SheetPickerGroup[] = [];
    /** Multi-file upload: true while the selected files are being read. */
    isReadingUploadFiles = false;
    private uploadSources: UploadSourceFile[] = [];
    private uploadFileName = '';
    /** Multi-sheet workbook: one draft per chosen sheet. Null for every other upload. */
    sheetDrafts: SheetDraftSet | null = null;
    /** Tables already written from this workbook, kept when a later sheet fails. */
    private uploadedSheetTables: string[] = [];
    uploadSheetProgress: { current: number; total: number; name: string } | null = null;
    /** New table: headers renamed because their names are reserved (shown as a note). */
    renamedHeaders: { from: string; to: string }[] = [];

    private worker?: Worker;
    private fileSizeKB = 0;
    private currentParseMode: 'upload' | 'append' | 'coreLogicImport' | null =
        null;
    private tablesNameSet = new Set<string>();

    private subscription?: ReturnType<
        StreamService['connectToServerSentEvents']
    > extends import('rxjs').Observable<infer T>
        ? import('rxjs').Subscription
        : never;

    readonly isDevEnv = !environment.production;
    private isDestroyed = false;

    private readonly cdr = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly zone = inject(NgZone);
    private readonly buttonLoadingService = inject(ButtonLoadingService);
    private readonly uploadFilesReader = inject(UploadFilesReaderService);

    constructor(
        @Inject(MAT_DIALOG_DATA) public data1: any,
        private dialogRef: MatDialogRef<AddTablesComponent>,
        private fb: FormBuilder,
        private tableService: TableService,
        private datasourceService: DataSourceService,
        private alert: CustomAlertService,
        private transloco: TranslocoService,
        private userService: UserService,
        private dialog: MatDialog,
        private confirmationService: FuseConfirmationService,
        private ngxCsvParser: NgxCsvParser,
        private fileService: TableFileService,
        private uploadService: TableUploadService,
        private tableStructureBackgroundService: TableStructureBackgroundService,
        private http: HttpClient,
        private kpiLogicService: KpiLogicService,
        private clipboard: Clipboard
    ) {
        // Track global loading state for button disabled state
        effect(() => {
            this.globalLoading = this.buttonLoadingService.loading();
            this.cdr.markForCheck();
        });

        if (typeof Worker !== 'undefined') {
            this.worker = new Worker(
                new URL('./file-parser.worker.ts', import.meta.url)
            );
            this.worker.onmessage = ({ data }) => {
                this.loading = false;
                if (this.currentParseMode === 'append') this.isAppendFileLoading = false;
                if (data?.error) {
                    if (data.error === 'missing header') this.dialogRef.close('missing header');
                    else this.alert.failedMessage(data.error);
                    this.cdr.markForCheck();
                    return;
                }
                if (this.currentParseMode === 'append') {
                    this.handleAppendParsedFromXlsx(data.data as string[][]);
                } else if (this.currentParseMode === 'coreLogicImport') {
                    this.handleCoreLogicParsedFromXlsx(data.data as string[][]);
                } else if (Array.isArray(data.sheets)) {
                    this.openSheetPicker([
                        {
                            fileName: this.uploadFileName,
                            kind: 'xlsx',
                            sizeKB: this.fileSizeKB,
                            sheets: data.sheets as WorkbookSheet[],
                            error: null,
                        },
                    ]);
                } else {
                    this.transformTableContent(data.data as string[][], 'xlsx');
                }
                this.cdr.markForCheck();
            };
            this.worker.onerror = () => {
                this.loading = false;
                if (this.currentParseMode === 'append') this.isAppendFileLoading = false;
                this.alert.failedMessage('Error parsing Excel file.');
                this.cdr.markForCheck();
            };
        }
    }

    ngOnInit(): void {
        this.userRole = (localStorage.getItem('userRole') || '')
            .toLowerCase()
            .trim();

        this.transloco.langChanges$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((lang) => {
                this.lang = lang;
                this.cdr.markForCheck();
            });

        this.form = this.fb.group({
            name: ['', [Validators.required, this.validateTableName, this.validateNameAvailable]],
            description: ['', [Validators.maxLength(10000)]],
            structure: ['', [Validators.maxLength(20000)]],
            allowAgentWrite: [false],
            columnTypes: this.fb.array([] as ColumnType[]),
            units: this.fb.array([] as (string | null)[]),
        });

        // Log initial structure formControl length
        const structureControl = this.form.get('structure');
        if (structureControl) {
            const initialValue = structureControl.value || '';
        }

        this.userService
            .getcurrency()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((cur) => {
                if (cur)
                    this.unitOptions.unshift({
                        name: `Currency (${cur})`,
                        value: `Currency (${cur})`,
                    });
                else
                    this.unitOptions.unshift({
                        name: 'Currency',
                        value: 'Currency',
                    });
            });

        this.bootstrapFromInput();
        this.resumePendingTableExtractionIfAny();
        this._syncTableNameReadonlyState();
        this.snapshotInitialExternalMeta();

        this.dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe((result) => {
                if (result) return;
                const refetch =
                    this.isExternalTableDialog() &&
                    (this.isExternalDescriptionStructureDirty() ||
                        this.savedExternalMetaThisVisit);
                this.data1?.onExternalDialogClosed?.({
                    refetchExternalTables: refetch,
                });
            });

        this.tableService
            .getAllTablesNames()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((res: string[]) => {
                this.tablesNameSet = new Set(res.map(v => v.toLowerCase()));
                this.form.get('name')?.updateValueAndValidity({ emitEvent: false });
                this.cdr.markForCheck();
            });

        this.form.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.refreshValidation());
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;
        this.subscription?.unsubscribe?.();
    }

    /** Header close button - dismiss without emitting a truthy save result. */
    closeAddTablesDialog(): void {
        if (this.isUploadLocked) {
            return;
        }
        // Sheets already written from a workbook must still refresh the list and get described.
        if (this.uploadedSheetTables.length) {
            this.closeAfterSheetUploads(this.uploadedSheetTables);
            return;
        }
        this.dialogRef.close();
    }

    get isSaveButtonDisabled(): boolean {
        return (
            this.loading ||
            this.loadingOnSubmit ||
            this.globalLoading ||
            this.savingExternalMeta
        );
    }

    get isSaveButtonVisible(): boolean {
        const type = this.data1?.type;

        if (this.userRole === 'company') {
            return (
                type === 'Add' ||
                type === 'View' ||
                this.isAppending ||
                this.isExternalTableDialog()
            );
        }

        return (
            (this.data1?.isMyTable && type === 'Add') ||
            (this.data1?.isMyTable && type === 'View') ||
            this.isAppending ||
            (this.data1?.isMyTable !== false && this.isExternalTableDialog())
        );
    }

    /** Shared / not-owned table (`isMyTable === false`): no meta edits, KPI edits, or append. */
    get isForeignReadOnlyTable(): boolean {
        const isMyTable = this.data1?.isMyTable;
        return isMyTable === false || isMyTable === 'false';
    }

    /** The agent-write checkbox: an existing internal table only, never external. */
    get showAgentWriteToggle(): boolean {
        // Agent writes to tables are paused for now.
        // return isAgentWriteTableDialog(
        //     this.data1?.type,
        //     this.isExternalTableDialog(),
        //     this.data1?.tableName
        // );
        return false;
    }

    get isExternalManagerStaffReadOnly(): boolean {
        return (
            this.isExternalTableDialog() &&
            (this.userRole === 'manager' || this.userRole === 'staff')
        );
    }

    /**
     * True when at least one footer action button would render.
     * Hides the whole sticky footer when every action is gated off (e.g. foreign read-only
     * View), so the dialog never shows an empty action bar.
     */
    get hasFooterActions(): boolean {
        if (this.isExternalManagerStaffReadOnly || this.isSheetPickerVisible || this.isReadingUploadFiles) {
            return false;
        }

        if (this.isAppending) {
            return true;
        }

        const type = this.data1?.type;
        const canMutateTable = this.data1?.isMyTable !== false;

        if (type === 'View' && canMutateTable) {
            return true;
        }

        if (canMutateTable && this.isExternalTableDialog()) {
            return true;
        }

        if (
            this.isDevEnv &&
            ((this.data1?.isMyTable && (type === 'Add' || type === 'View')) ||
                this.isAppending)
        ) {
            return true;
        }

        return this.isSaveButtonVisible;
    }

    get isTableNameReadOnly(): boolean {
        // Keep same editability rule used by action/edit controls.
        return (
            this.isForeignReadOnlyTable || this.isExternalManagerStaffReadOnly
        );
    }

    /** Use single-column layout for Add mode (except append mode). */
    get isAddModeSingleColumnLayout(): boolean {
        return this.data1?.type === 'Add' && !this.isAppending;
    }

    /**
     * Always reserve the preview column in View/Append/external modes so loading and
     * empty states fill the right pane (CSS grid still paints an empty second column
     * when the aside is omitted). Add mode only shows the pane after headers exist.
     */
    get showTablePreviewPane(): boolean {
        return !this.isAddModeSingleColumnLayout || this.headers.length > 0;
    }

    /** User-collapsed preview pane in split layout (View / Append / external). */
    isTablePreviewCollapsed = true;

    /** Split layout only — not Add mode single-column stack. */
    get canToggleTablePreviewCollapse(): boolean {
        return this.showTablePreviewPane && !this.isAddModeSingleColumnLayout;
    }

    get tablePreviewCollapseTooltipKey(): string {
        return this.isTablePreviewCollapsed
            ? 'Show table preview'
            : 'Hide table preview';
    }

    get tablePreviewCollapseChevronIcon(): string {
        const collapseIcon =
            this.lang === 'ar'
                ? 'heroicons_outline:chevron-left'
                : 'heroicons_outline:chevron-right';
        const expandIcon =
            this.lang === 'ar'
                ? 'heroicons_outline:chevron-right'
                : 'heroicons_outline:chevron-left';
        return this.isTablePreviewCollapsed ? expandIcon : collapseIcon;
    }

    toggleTablePreviewCollapsed(): void {
        this.isTablePreviewCollapsed = !this.isTablePreviewCollapsed;
    }

    /** Preview-pane fetch (not submit / append-file parse). */
    get isTablePreviewLoading(): boolean {
        return this.loading && !this.loadingOnSubmit && !this.isAppendFileLoading;
    }

    get isUploadOverlayVisible(): boolean {
        return (
            this.isTableUploadActive ||
            (this.loading && this.isAppendFileLoading)
        );
    }

    get uploadProgressStatusKey(): string {
        if (this.isFinalizing) {
            return 'Finalizing...';
        }
        if (this.uploadStatus === 'inserting') {
            return 'Inserting data...';
        }
        if (this.showProgress) {
            return 'Uploading data...';
        }
        return 'Preparing upload...';
    }

    private _syncTableNameReadonlyState(): void {
        const nameCtrl = this.form?.get('name');
        if (!nameCtrl) return;
        if (this.isTableNameReadOnly) {
            nameCtrl.disable({ emitEvent: false });
            return;
        }
        nameCtrl.enable({ emitEvent: false });
    }

    private bootstrapFromInput(): void {
        this.metricDefinitions = this.getKpiLogicFromInput();
        const lockedColumns = this.normalizeLockedColumns(this.data1);
        this.lockedStructureColumns = new Set(lockedColumns);
        this.initialLockedStructureColumns = new Set(lockedColumns);
        if (this.data1?.files?.length) void this.readUploadFiles(this.data1.files);
        else if (this.data1?.event) this.onFileChange(this.data1.event);
        if (this.isForeignReadOnlyTable) {
            this.form.get('allowAgentWrite')?.disable({ emitEvent: false });
        }

        if (this.data1?.type === 'View') {
            this.form.patchValue({
                name:
                    this.data1.tableInfo.userTableName ||
                    this.data1.tableInfo.userTable,
                structure: this.data1.tableInfo.description || '',
                description: this.data1.tableInfo.about || '',
            });
            if (
                Array.isArray(this.data1.tableContent) &&
                this.data1.tableContent.length
            ) {
                this.headers = this.sanitizeHeaders(
    Object.keys(this.data1.tableContent[0])
);            }
            this.parseStructureToTable();
        } else if (this.isExternalTableDialog()) {
            this.form.patchValue({
                name:
                    this.data1.tableInfo?.value ??
                    this.data1.tableName ??
                    '',
                structure: this.data1.tableInfo?.description ?? '',
                description: this.data1.tableInfo?.about ?? '',
            });
            this.parseStructureToTable();
        }

        if (this.data1?.tableName) {
            if (this.isExternalTableDialog()) {
                this.loadExternalTablePreviewWithMeta(
                    this.data1.tableName,
                    this.pageIndex,
                    this.pageSize,
                    this.data1.dataSourceId
                );
            } else if (this.data1.type === 'View') {
                this.loadViewTablePreviewWithMeta(
                    this.data1.tableName,
                    this.pageIndex,
                    this.pageSize
                );
            }
        }

        if (!(this.isExternalTableDialog() && this.data1?.tableName)) {
            this.hydrateExternalTableMetaIfNeeded();
        }
    }

    /**
     * External engine dialogs opened with only a table name (e.g. agent contact-info)
     * lack `tableInfo.description` / `about`. Load saved meta from `external-tables/get-tables`,
     * then fall back to generate-table-desc when still empty and the table is editable.
     */
    private hydrateExternalTableMetaIfNeeded(): void {
        if (!this.isExternalTableDialog()) {
            return;
        }
        const tableName = this.data1?.tableName;
        const dataSourceId = this.data1?.dataSourceId;
        if (!tableName || dataSourceId == null || dataSourceId === '') {
            return;
        }

        const structureEmpty = !String(
            this.form.get('structure')?.value ?? ''
        ).trim();
        const descriptionEmpty = !String(
            this.form.get('description')?.value ?? ''
        ).trim();
        if (!structureEmpty && !descriptionEmpty) {
            return;
        }

        this.datasourceService
            .getExternalTablesInDatasource(String(dataSourceId))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: async (res: any) => {
                    this.applyExternalTableListRefresh(res, String(tableName));
                    const stillNeed =
                        !String(
                            this.form.get('structure')?.value ?? ''
                        ).trim() ||
                        !String(
                            this.form.get('description')?.value ?? ''
                        ).trim();
                    if (stillNeed && !this.isForeignReadOnlyTable) {
                        await this.extractTableStructure();
                    }
                    this.snapshotInitialExternalMeta();
                    this.cdr.markForCheck();
                },
                error: async () => {
                    if (!this.isForeignReadOnlyTable) {
                        await this.extractTableStructure();
                    }
                    this.snapshotInitialExternalMeta();
                    this.cdr.markForCheck();
                },
            });
    }

    private normalizeKpiSourceArray(source: unknown): KpiLogicItem[] {
        if (!Array.isArray(source)) return [];
        return source
            .filter(item => item && typeof item === 'object')
            .map((item: any) => ({
                id: item.id ?? item.kpi_id ?? item.kpiId,
                level: item.level ?? item.kpi_level ?? item.kpiLevel ?? undefined,
                kpi_name: String(
                    item.kpi_name ?? item.kpiName ?? item.name ?? ''
                ),
                calculation: String(
                    item.calculation ?? item.formula ?? item.expression ?? ''
                ),
                description: String(
                    item.description ?? item.desc ?? item.about ?? ''
                ),
                isLocked: Boolean(
                    item.isLocked ?? item.locked ?? item.is_locked
                ),
            }))
            .filter(item => item.kpi_name || item.calculation || item.description);
    }

    private getKpiLogicFromInput(): KpiLogicItem[] {
        const source =
            this.data1?.tableInfo?.kpiLogic ??
            this.data1?.tableInfo?.kpis ??
            this.data1?.table?.kpiLogic ??
            this.data1?.table?.kpis ??
            this.data1?.kpiLogic ??
            this.data1?.kpis;
        return this.normalizeKpiSourceArray(source);
    }

    /** Apply `get-table-meta` / `get-external-table-meta` payload to structure, about, and KPIs. */
    private applyTableMetaResponse(meta: any | null | undefined): void {
        if (!meta || typeof meta !== 'object') {
            return;
        }
        const payload =
            meta?.data && typeof meta.data === 'object' && !Array.isArray(meta.data)
                ? meta.data
                : meta;
        const about = payload.about;
        const description =
            payload.description ?? payload.table_description ?? payload.structure;
        const kpis = payload.kpis ?? payload.kpiLogic;
        const lockedColumns = this.normalizeLockedColumns(payload);
        const columnDataQualityFlags =
            payload.column_data_quality_flags ?? payload.columnDataQualityFlags;

        const savedAgentWrite = readAllowAgentWrite(payload);
        if (savedAgentWrite !== null && this.showAgentWriteToggle) {
            this.initialAllowAgentWrite = savedAgentWrite;
            this.form.patchValue(
                { allowAgentWrite: savedAgentWrite },
                { emitEvent: false }
            );
        }

        const patches: Record<string, string> = {};
        if (description !== undefined && description !== null) {
            patches.structure = String(description);
        }
        if (about !== undefined && about !== null) {
            patches.description = String(about);
        }
        if (Object.keys(patches).length) {
            this.form.patchValue(patches, { emitEvent: false });
        }
        if (Array.isArray(kpis) && kpis.length) {
            this.metricDefinitions = this.normalizeKpiSourceArray(kpis);
            if (this.data1?.tableInfo) {
                this.data1.tableInfo = {
                    ...this.data1.tableInfo,
                    kpis: [...kpis],
                    kpiLogic: [...kpis],
                };
            }
        }
        this.fileService.mergeApiHeaderRecord(
            this.apiSchema,
            payload.schema as Record<string, unknown> | undefined
        );
        this.fileService.mergeApiHeaderRecord(
            this.metaApiComments,
            payload.comments as Record<string, unknown> | undefined
        );
        this.fileService.mergeApiHeaderRecord(
            this.apiComments,
            payload.comments as Record<string, unknown> | undefined
        );

        this.lockedStructureColumns = new Set(lockedColumns);
        this.initialLockedStructureColumns = new Set(lockedColumns);
        this.pendingLockStructureColumns.clear();
        this.pendingUnlockStructureColumns.clear();
        if (this.data1?.tableInfo) {
            this.data1.tableInfo = {
                ...this.data1.tableInfo,
                lockedColumns: [...lockedColumns],
                ...(columnDataQualityFlags
                    ? { column_data_quality_flags: columnDataQualityFlags }
                    : {}),
            };
        }
        this.parseStructureToTable();
        this.cdr.markForCheck();
    }

    private normalizeLockedColumns(meta: any): string[] {
        const source =
            meta?.lockedColumns ??
            meta?.locked_columns ??
            this.data1?.tableInfo?.lockedColumns ??
            this.data1?.tableInfo?.locked_columns ??
            this.data1?.table?.lockedColumns ??
            this.data1?.table?.locked_columns;
        if (!Array.isArray(source)) {
            return [];
        }
        return source
            .map(item => String(item ?? '').trim())
            .filter(Boolean);
    }

    // ---------------- file handling ----------------
    onFileChange(evt: any): void {
        const file: File | undefined = evt?.target?.files?.[0];
        if (!file) return;

        this.loading = true;
        this.fileSizeKB = +(file.size / 1024).toFixed(2);
        this.currentParseMode = 'upload';

        const cleanName = this.generateTableNameFromFile(file.name);
        this.form.get('name')!.setValue(cleanName);
        this.uploadFileName = file.name;

        const kind = uploadFileKind(file);
        if (kind === 'csv') {
            this.ngxCsvParser
                .parse(file, { header: true, delimiter: ',', encoding: 'utf8' })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (rows: any[] | NgxCSVParserError) => {
                        this.loading = false;
                        this.cdr.markForCheck();

                        if (!Array.isArray(rows) || !rows.length) {
                            return;
                        }
                        if (
                            Object.keys(rows[0]).some(k =>
                                Number.isInteger(Number(k))
                            )
                        ) {
                            this.dialogRef.close('missing header');
                            return;
                        }
                        const processedRows =
                            this.fileService.processCSVDataForAccountingFormat(
                                rows
                            );
                        this.transformTableContent(processedRows);
                    },
                    error: (_e: NgxCSVParserError) => {
                        this.loading = false;
                        this.alert.failedMessage('CSV parse error');
                    },
                });
            return;
        }

        if (kind === 'xlsx') {
            if (!this.worker) {
                this.loading = false;
                this.alert.failedMessage('Worker unavailable');
                return;
            }
            // Custom definitions stay one table per file.
            this.worker.postMessage({ file, allSheets: !this.data1?.customDefinitionSourceType });
            return;
        }

        this.loading = false;
        this.alert.failedMessage('Unsupported file type');
    }

    private transformTableContent(
        src: any[] | string[][],
        kind?: 'xlsx'
    ): void {
        if (!this.headers.length) {
this.headers = this.sanitizeHeaders(
    kind === 'xlsx'
        ? (src as string[][])[0].map(h => String(h).trim())
        : Object.keys((src as any[])[0])
);
            this.renameReservedHeadersForNewTable();
        }

        const rowsAA: any[][] =
            kind === 'xlsx'
                ? this.fileService.rowsFromXlsxAoA(src as string[][]).rows
                : this.fileService.rowsFromCsvObjects(src as any[]).rows;

        this.allRows = rowsAA;
        this.types = this.fileService.inferTypesFromSample(
            this.headers,
            this.allRows
        );
        this.editStates = this.headers.map(() => false);
        this.initColumnTypeControls();
        this.captureOriginalSchemaOnce();
        this.updatePageData();
        this.loading = false;
    }

    // ---------------- server fetch (view mode) ----------------
    private getTableByName(name: string, page: number, size: number): void {
        this.loading = true;
        this.tableService
            .getTableByName(name, page, size)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res: any) => {
                    this.hydrateServerPage(res);
                    this.loading = false;
                    this.cdr.markForCheck();
                },
                error: (error) => {
                    const msg = this.toMessage(error?.error);
                    this.alert.failedMessage(msg);
                    this.loading = false;
                    this.cdr.markForCheck();
                },
            });
    }

    /** Initial internal table open: grid from `get-table-data` + meta from `get-table-meta`. */
    private loadViewTablePreviewWithMeta(
        name: string,
        page: number,
        size: number
    ): void {
        this.headers = [];
        this.types = [];
        this.loading = true;
        forkJoin({
            preview: this.tableService
                .getTableByName(name, page, size)
                .pipe(take(1)),
            meta: this.tableService.getTableMeta(name).pipe(
                take(1),
                catchError(() => of(null))
            ),
        })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ preview, meta }) => {
                    this.hydrateServerPage(preview);
                    this.applyTableMetaResponse(meta);
                    this.loading = false;
                    this.cdr.markForCheck();
                },
                error: (error) => {
                    const msg = this.toMessage(error?.error);
                    this.alert.failedMessage(msg);
                    this.loading = false;
                    this.cdr.markForCheck();
                },
            });
    }

    private toMessage(err: any): string {
        if (err instanceof ArrayBuffer) {
            const text = new TextDecoder('utf-8').decode(new Uint8Array(err));
            try {
                return JSON.parse(text).message || text;
            } catch {
                return text;
            }
        }
        if (typeof err === 'string') return err;
        return err?.message || 'Unknown error';
    }

    /** Paginated external preview only (meta does not depend on page). */
    private getExternalTableData(
        name: string,
        page: number,
        size: number,
        datasourceId?: string
    ): void {
        this.loading = true;
        this.tableService
            .getExternalTableData(name, page, size, datasourceId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res: any) => {
                    this.hydrateServerPage(res);
                },
                error: (error) => {
                    const msg = this.toMessage(error?.error);
                    this.alert.failedMessage(msg);
                    this.loading = false;
                    this.cdr.markForCheck();
                },
            });
    }

    /** Initial external table open: preview + `get-external-table-meta`. */
    private loadExternalTablePreviewWithMeta(
        name: string,
        page: number,
        size: number,
        datasourceId?: string
    ): void {
        const ds =
            datasourceId != null && String(datasourceId).length > 0
                ? String(datasourceId)
                : '';
        this.loading = true;
        const meta$ =
            ds.length > 0
                ? this.tableService
                      .getExternalTableMeta(name, ds)
                      .pipe(take(1), catchError(() => of(null)))
                : of(null);

        this.headers = [];
        this.types = [];
        forkJoin({
            preview: this.tableService
                .getExternalTableData(name, page, size, datasourceId)
                .pipe(take(1)),
            meta: meta$,
        })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ({ preview, meta }) => {
                    this.hydrateServerPage(preview);
                    this.applyTableMetaResponse(meta);
                    this.loading = false;
                    this.hydrateExternalTableMetaIfNeeded();
                    this.watchForSourceDriftReExtraction(name, ds);
                    this.cdr.markForCheck();
                },
                error: (error) => {
                    const msg = this.toMessage(error?.error);
                    this.alert.failedMessage(msg);
                    this.loading = false;
                    this.hydrateExternalTableMetaIfNeeded();
                    this.cdr.markForCheck();
                },
            });
    }

    /**
     * Close the preview when the server starts re-extracting this table.
     *
     * Opening an external table also reconciles its stored structure against the
     * source. If the source changed, the backend kicks off a re-extraction and
     * publishes the same `table-description` entry a manual extraction does - so
     * the preview on screen is describing a structure that is being replaced
     * underneath it. Closing here mirrors `extractTableStructure()` exactly,
     * which is what makes the parent list show its normal extracting state.
     *
     * Only a not-pending -> pending transition closes. A table already extracting
     * when the dialog opened is handled by `resumePendingTableExtractionIfAny()`,
     * which deliberately keeps it open behind a loader.
     */
    private watchForSourceDriftReExtraction(
        tableName: string,
        dataSourceId: string
    ): void {
        if (!tableName || !dataSourceId) {
            return;
        }
        this.tableStructureBackgroundService
            .whenExtractionStarts({ tableName, dataSourceId })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (this.isDestroyed) {
                    return;
                }
                this.alert.successMessage(
                    'The source structure changed. Re-extracting this table in the background.'
                );
                this.dialogRef.close({
                    tableStructureExtractionStarted: true,
                    tableName,
                    dataSourceId,
                });
            });
    }

    private hydrateServerPage(res: any): void {
        const { data = [], count = 0, schema = {}, comments = {} } = res || {};

        if (!this.headers.length && Array.isArray(data) && data.length) {
            this.headers = this.sanitizeHeaders(Object.keys(data[0]));
        }

        if (Object.keys(schema).length > 0) {
            this.fileService.mergeApiHeaderRecord(this.apiSchema, schema);
        }
        if (Object.keys(comments).length > 0) {
            this.fileService.mergeApiHeaderRecord(this.apiComments, comments);
        }

        const pageRows = data.map((row: any) =>
            this.headers.map((h) => {
                const v = row[h];
                if (h === 'manufacturingdate' || h === 'expirydate')
                    return v ? String(v).split('T')[0] : null;
                return v === '' || v === undefined ? null : v;
            })
        );

        this.rows = pageRows;
        this.length = count;

        if (!this.types.length) {
            if (Object.keys(this.apiSchema).length > 0) {
                this.types = this.headers.map((h) => {
                    const cleanH = this.fileService.cleanHeaderKey(h);
                    const apiType = this.apiSchema[cleanH] || this.apiSchema[h];
                    if (apiType) {
                        return this.fileService.normalizeApiTypeToColumnType(
                            apiType
                        );
                    }
                    return this.fileService.detectTypeForColumn(
                        this.rows,
                        this.headers.indexOf(h)
                    );
                });
            } else {
                this.types = this.fileService.inferTypesFromSample(
                    this.headers,
                    this.rows
                );
            }
            this.initColumnTypeControls();
            this.captureOriginalSchemaOnce();
        }

        this.loading = false;
        this.cdr.markForCheck();
    }

    // ---------------- pagination ----------------
    onPageChange(e: PageEvent): void {
        this.pageIndex = e.pageIndex;
        this.pageSize = e.pageSize;

        if (this.data1?.type === 'View') {
            this.getTableByName(
                this.data1.tableName,
                this.pageIndex,
                this.pageSize
            );
        } else if (this.isExternalTableDialog()) {
            this.getExternalTableData(
                this.data1.tableName,
                this.pageIndex,
                this.pageSize,
                this.data1.dataSourceId
            );
        } else {
            this.updatePageData();
        }
    }

    private updatePageData(): void {
        const start = this.pageIndex * this.pageSize;
        const end = start + this.pageSize;
        this.rows = this.allRows.slice(start, end);
        this.length = this.allRows.length;

        if (!this.types.length && this.rows.length) {
            this.types = this.fileService.inferTypesFromSample(
                this.headers,
                this.rows
            );
            this.initColumnTypeControls();
            this.captureOriginalSchemaOnce();
        }
    }

    // ---------------- column typing / form ----------------
    private initColumnTypeControls(savedUnits?: (string | null)[]): void {
        const columnTypes = this.form.get('columnTypes') as FormArray;
        const units = this.form.get('units') as FormArray;
        columnTypes.clear();
        units.clear();

        this.types.forEach((t, i) => {
            columnTypes.push(this.fb.control<ColumnType>(t));
            if (t === 'DATE')
                units.push(
                    this.fb.control<string | null>(savedUnits?.[i] ?? null, Validators.required)
                );
            else units.push(this.fb.control<string>(savedUnits?.[i] ?? 'No Unit'));
        });
    }

    onTypeChange(index: number, value: ColumnType): void {
        const columnTypes = this.form.get('columnTypes') as FormArray;
        const units = this.form.get('units') as FormArray;

        columnTypes.at(index).setValue(value as any);
        this.types[index] = value;

        if (value === 'DATE') {
            units.at(index).setValue(null);
            units.at(index).setValidators([Validators.required]);
        } else {
            units.at(index).setValue('No Unit');
            units.at(index).clearValidators();
        }
        units.at(index).updateValueAndValidity({ emitEvent: false });
    }

    // ---------------- header editing ----------------
    toggleEdit(i: number): void {
        if (this.data1?.type !== 'Add' && !this.isAppending) return;
        this.editStates[i] = !this.editStates[i];
        if (!this.editStates[i]) {
            const el = this.headerSpans.get(i)?.nativeElement;
            if (el) this.updateHeader(i, el.innerText);
        }
    }

    updateHeader(index: number, value: string): void {
        this.headers[index] = value;
        this.renameReservedHeadersForNewTable();
        this.refreshValidation();
    }

    // ---------------- new-table validation ----------------
    get isNewTableUpload(): boolean {
        return this.data1?.type === 'Add' && !this.isAppending;
    }

    /** Custom definitions are external tables; the backend owns their name uniqueness (409). */
    private get existingNamesForCheck(): ReadonlySet<string> {
        return this.data1?.customDefinitionSourceType ? new Set<string>() : this.tablesNameSet;
    }

    private otherDraftNames(): string[] {
        const set = this.sheetDrafts;
        const activeKey = set?.active?.key;
        return set ? set.drafts.filter(d => d.key !== activeKey).map(d => d.name) : [];
    }

    /** Name field: taken by another table, or used by another selected sheet. */
    private readonly validateNameAvailable = (ctrl: AbstractControl): ValidationErrors | null => {
        if (!this.data1 || !this.isNewTableUpload) {
            return null;
        }
        const problem = tableNameProblem(ctrl.value, this.existingNamesForCheck, this.otherDraftNames());
        if (problem === 'taken') return { nameTaken: true };
        if (problem === 'duplicate') return { duplicateName: true };
        return null;
    };

    private currentTableProblems(): TableProblems {
        return {
            name: tableNameProblem(this.form.get('name')?.value, this.existingNamesForCheck, this.otherDraftNames()),
            columns: columnProblems(
                this.headers,
                this.columnTypesFA.value as ColumnType[],
                this.unitsFA.value as (string | null)[],
                this.fileService
            ),
        };
    }

    /** Recomputes the highlighted columns and the active tab's count; runs on every edit. */
    private refreshValidation(): void {
        if (!this.form || !this.isNewTableUpload) {
            return;
        }
        const problems = this.currentTableProblems();
        this.columnProblemList = problems.columns;
        const activeKey = this.sheetDrafts?.active?.key;
        if (activeKey) {
            this.draftProblemCounts.set(activeKey, problemCount(problems));
        }
        this.cdr.markForCheck();
    }

    isColumnInvalid(index: number, kind?: ColumnProblem): boolean {
        const list = this.columnProblemList[index];
        return this.showValidation && !!list?.length && (!kind || list.includes(kind));
    }

    /** Short note shown under a highlighted column's name: its first problem. */
    columnProblemNote(index: number): string {
        const first = this.columnProblemList[index]?.[0];
        return first ? this.transloco.translate(COLUMN_PROBLEM_LABEL[first]) : '';
    }

    columnProblemTooltip(index: number): string {
        if (!this.showValidation) {
            return '';
        }
        return (this.columnProblemList[index] ?? []).map(p => this.transloco.translate(COLUMN_PROBLEM_TEXT[p])).join(' ');
    }

    sheetProblemCount(key: string): number {
        return this.showValidation ? this.draftProblemCounts.get(key) ?? 0 : 0;
    }

    /** The account's table names, read again so a table created since the dialog opened counts. */
    private async refreshExistingTableNames(): Promise<void> {
        try {
            const names = await firstValueFrom(this.tableService.getAllTablesNames());
            this.tablesNameSet = new Set((names ?? []).map((v: string) => String(v).toLowerCase()));
        } catch {
            // Keep the list loaded with the dialog.
        }
    }

    /** Highlights every problem, opens the first one, and lists them all in one dialog. */
    private reportProblems(
        tables: { label: string; headers: readonly string[]; problems: TableProblems }[],
        onScreen: TableProblems
    ): void {
        this.form.get('name')?.markAsTouched();
        this.form.get('name')?.updateValueAndValidity({ emitEvent: false });
        this.unitsFA.markAllAsTouched();
        this.refreshValidation();
        const column = firstProblemColumn(onScreen);
        if (column >= 0) {
            setTimeout(() => this.scrollToHeader(column));
        }
        this.openInfoDialog(
            this.transloco.translate('Fix these before uploading'),
            describeTableProblems(tables, k => this.transloco.translate(k))
        );
    }

    /** A new table never keeps a reserved column name: rename it and say so, instead of blocking Save. */
    private renameReservedHeadersForNewTable(): void {
        if (this.data1?.type !== 'Add' || this.isAppending) {
            return;
        }
        const { headers, renamed } = this.fileService.renameReservedHeaders(this.headers);
        if (renamed.length) {
            this.headers = headers;
            this.renamedHeaders = [...this.renamedHeaders, ...renamed];
            this.cdr.markForCheck();
        }
    }

    get renamedHeadersSummary(): string {
        return this.renamedHeaders.map(r => `${r.from} → ${r.to}`).join(', ');
    }

    // ---------------- submit ----------------
    async submit(): Promise<void> {
        if (this.isForeignReadOnlyTable) {
            return;
        }
        if (this.data1?.type === 'View' && !this.isAppending) {
            this.loadingOnSubmit = true;
            this.updateTableDescription();
            return;
        }

        if (this.isExternalTableDialog() && !this.isAppending) {
            this.saveExternalTableDescription();
            return;
        }

        if (this.sheetDrafts) {
            await this.submitSheetDrafts();
            return;
        }
        if (!(await this.validateTableForCreate())) {
            return;
        }

        try {
            this.beginTableUpload();
            const payload = this.preparePayload();
            payload.data = this.fileService.mapRowsToObjects(
                this.headers,
                this.allRows
            );
            // External "Upload as Custom Definition": same parse + gzip, different endpoint + sourceType.
            if (this.data1?.customDefinitionSourceType) {
                (payload as unknown as { sourceType: string }).sourceType =
                    this.data1.customDefinitionSourceType;
                const customFd = this.uploadService.compressToFormData(
                    payload,
                    this.fileSizeKB
                );
                await firstValueFrom(
                    this.datasourceService.uploadAsCustomDefinition(customFd)
                );
                this.alert.successMessage(
                    'Custom definition created successfully!'
                );
                this.dialogRef.close(true);
                return;
            }
            const uploadResult = await this.uploadTablePayload(payload);

            this.alert.successMessage(
                this.isAppending
                    ? 'Data has been appended successfully!'
                    : 'Table has been created successfully!'
            );
            // Prefer the technical name from upload-internal-table-progress for generate-table-desc.
            const tableName =
                String(uploadResult?.tableName ?? '').trim() ||
                this.resolveInternalTableNameForExtraction();
            const dataSourceId = this.data1?.dataSourceId;
            if (
                tableName &&
                dataSourceId != null &&
                dataSourceId !== ''
            ) {
                this.dialogRef.close({
                    afterInternalTableUpload: true,
                    tableName,
                    dataSourceId,
                });
            } else {
                this.dialogRef.close(true);
            }
        } catch (e: any) {
            console.error(e);
            this.alert.failedMessage(
                e?.error?.message || e?.message || 'Upload failed'
            );
        } finally {
            this.isTableUploadActive = false;
            this.loadingOnSubmit = false;
            this.loading = false;
            this.cdr.markForCheck();
        }
    }

    /** Checks a new table must pass before upload. Highlights and lists every problem. */
    private async validateTableForCreate(): Promise<boolean> {
        await this.refreshExistingTableNames();
        this.showValidation = true;
        const problems = this.currentTableProblems();
        if (!problemCount(problems)) {
            this.refreshValidation();
            return true;
        }
        const label = String(this.form.get('name')?.value || this.uploadFileName || '');
        this.reportProblems([{ label, headers: this.headers, problems }], problems);
        return false;
    }

    private beginTableUpload(): void {
        this.loadingOnSubmit = true;
        this.isTableUploadActive = true;
        this.progressValue = 0;
        this.uploadStatus = '';
        this.showProgress = false;
        this.isFinalizing = false;
        this.cdr.markForCheck();
    }

    private uploadTablePayload(payload: UploadPayload): Promise<TableUploadDoneInfo> {
        const settle = () => {
            this.showProgress = false;
            this.isFinalizing = false;
            this.uploadStatus = '';
            this.isTableUploadActive = false;
            this.loadingOnSubmit = false;
            this.cdr.markForCheck();
        };
        return this.uploadService.upload(
            this.uploadService.compressToFormData(payload, this.fileSizeKB),
            {
                onProgress: (v) => {
                    this.showProgress = true;
                    this.progressValue = v;
                    this.cdr.markForCheck();
                },
                onStatusChange: (status) => {
                    this.uploadStatus = status;
                    this.cdr.markForCheck();
                },
                onFinalizingChange: (f) => {
                    this.isFinalizing = f;
                    this.cdr.markForCheck();
                },
                onDone: settle,
                onError: settle,
            }
        );
    }

    // ---------------- multi-sheet workbook ----------------
    get isSheetPickerVisible(): boolean {
        return this.pickerGroups.length > 0 && !this.sheetDrafts;
    }

    /** Several files chosen on the tables page: read each into sheets, then let the user pick. */
    private async readUploadFiles(files: File[]): Promise<void> {
        this.loading = true;
        this.isReadingUploadFiles = true;
        this.cdr.markForCheck();
        try {
            this.openSheetPicker(await this.uploadFilesReader.readFiles(files));
        } finally {
            this.loading = false;
            this.isReadingUploadFiles = false;
            this.cdr.markForCheck();
        }
    }

    private openSheetPicker(files: UploadSourceFile[]): void {
        this.uploadSources = files;
        this.pickerGroups = buildPickerGroups(files);
        this.form.get('name')?.setValue('', { emitEvent: false });
    }

    /** Picker confirmed: one draft per chosen sheet, the first one opened for editing. */
    onWorkbookSheetsChosen(indexes: number[]): void {
        const drafts = draftsForChosenSheets(this.uploadSources, indexes, this.fileService, this.tablesNameSet);
        if (!drafts.length) {
            return;
        }
        this.uploadSources = [];
        this.sheetDrafts = new SheetDraftSet(drafts);
        this.loadSheetDraft(drafts[0]);
    }

    /** Opens another sheet, keeping every edit made on the one being left. */
    selectSheetDraft(index: number): void {
        if (!this.sheetDrafts || index === this.sheetDrafts.activeIndex) {
            return;
        }
        this.loadSheetDraft(this.sheetDrafts.switchTo(index, this.currentSheetDraftEdits()));
    }

    private currentSheetDraftEdits(): SheetDraftEdits {
        return {
            name: String(this.form.get('name')?.value ?? ''),
            description: String(this.form.get('description')?.value ?? ''),
            headers: this.headers,
            types: this.columnTypesFA.value as ColumnType[],
            units: this.unitsFA.value as (string | null)[],
            editStates: this.editStates,
            renamedHeaders: this.renamedHeaders,
        };
    }

    private loadSheetDraft(draft: SheetDraft): void {
        this.headers = [...draft.headers];
        this.allRows = draft.rows as unknown[][];
        this.types = [...draft.types];
        this.editStates = [...draft.editStates];
        this.renamedHeaders = [...draft.renamedHeaders];
        this.fileSizeKB = draft.sizeKB;
        this.initColumnTypeControls(draft.units);
        this.form.patchValue({ name: draft.name, description: draft.description });
        this.form.get('name')?.markAsTouched();
        if (this.showValidation) {
            this.unitsFA.markAllAsTouched();
        }
        this.pageIndex = 0;
        if (this.paginator) {
            this.paginator.pageIndex = 0;
        }
        this.updatePageData();
        this.cdr.markForCheck();
    }

    /** Validates every chosen sheet, then writes one table per sheet, in order. */
    private async submitSheetDrafts(): Promise<void> {
        const set = this.sheetDrafts!;
        set.store(this.currentSheetDraftEdits());
        await this.refreshExistingTableNames();
        const existing = this.existingNamesForCheck;
        const checks = set.drafts.map(d => ({
            key: d.key,
            label: d.label,
            headers: d.headers,
            problems: {
                name: tableNameProblem(
                    d.name,
                    existing,
                    set.drafts.filter(o => o.key !== d.key).map(o => o.name)
                ),
                columns: columnProblems(d.headers, d.types, d.units, this.fileService),
            } as TableProblems,
        }));
        this.showValidation = true;
        checks.forEach(c => this.draftProblemCounts.set(c.key, problemCount(c.problems)));
        const firstBad = checks.findIndex(c => problemCount(c.problems) > 0);
        if (firstBad >= 0) {
            this.selectSheetDraft(firstBad);
            this.reportProblems(checks, checks[firstBad].problems);
            return;
        }

        const written: string[] = [];
        const total = set.drafts.length;
        this.sheetBatchRunning = true;
        this.syncUploadLock();
        try {
            for (let i = 0; i < total; i++) {
                this.selectSheetDraft(i);
                this.uploadSheetProgress = { current: i + 1, total, name: set.drafts[i].label };
                this.beginTableUpload();
                const payload = this.preparePayload();
                payload.data = this.fileService.mapRowsToObjects(this.headers, this.allRows);
                const result = await this.uploadTablePayload(payload);
                this.uploadedSheetTables.push(String(result?.tableName ?? '').trim() || payload.name);
                this.tablesNameSet.add(String(payload.name).toLowerCase());
                written.push(set.drafts[i].key);
            }
            this.alert.successMessage('Tables have been created successfully!');
            this.closeAfterSheetUploads(this.uploadedSheetTables);
        } catch (e: unknown) {
            const err = e as { error?: { message?: string }; message?: string } | null;
            const failed = set.drafts[written.length]?.label ?? '';
            const reason = this.transloco.translate(err?.error?.message || err?.message || 'Upload failed');
            this.alert.failedMessage(
                this.transloco.translate('Sheet "{{name}}" was not uploaded: {{reason}}', { name: failed, reason }),
                undefined,
                false
            );
            // Written sheets leave the list so a retry sends only the rest.
            set.removeSheets(written);
            if (set.active) {
                this.loadSheetDraft(set.active);
            }
        } finally {
            this.uploadSheetProgress = null;
            this.sheetBatchRunning = false;
            this.isTableUploadActive = false;
            this.loadingOnSubmit = false;
            this.loading = false;
            this.cdr.markForCheck();
        }
    }

    private closeAfterSheetUploads(tableNames: string[]): void {
        const dataSourceId = this.data1?.dataSourceId;
        if (dataSourceId == null || dataSourceId === '') {
            this.dialogRef.close(true);
            return;
        }
        this.dialogRef.close({
            afterInternalTableUpload: true,
            tableName: tableNames[0],
            tableNames,
            dataSourceId,
        });
    }

    private get effectiveApiComments(): Record<string, string> {
        return { ...this.apiComments, ...this.metaApiComments };
    }

    /** Download the gzip file that would be sent to the backend (same as upload payload). */
    downloadGzip(): void {
        let payload: UploadPayload;
        if (this.isAppending) {
            const dateColumns = this.fileService.buildAppendDateColumns({
                originalHeaders: this.originalHeaders,
                originalHeadersClean: this.originalHeadersClean,
                originalTypes: this.originalTypes,
                apiSchema: this.apiSchema,
                apiComments: this.effectiveApiComments,
            });
            payload = {
                name: this.form.get('name')!.value,
                description: this.form.get('description')!.value,
                dataSourceId: this.data1?.dataSourceId,
                table: Object.fromEntries(
                    this.originalHeaders.map((h, i) => [
                        this.fileService.cleanHeaderKey(h),
                        this.originalTypes[i],
                    ])
                ),
                comments: this.fileService.buildAppendComments(
                    this.originalHeaders,
                    this.effectiveApiComments
                ),
                dateColumns,
                append: true,
                data: this.fileService.mapRowsToObjects(
                    this.originalHeaders,
                    this.allRows
                ),
            };
        } else {
            payload = this.preparePayload();
            payload.data = this.fileService.mapRowsToObjects(
                this.headers,
                this.allRows
            );
        }
        const file = this.uploadService.getGzipFile(
            payload,
            this.fileSizeKB
        );
        const url = URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
    }

    /** Row change history of this table (agent writes and their reverts). */
    openTableHistory(): void {
        const tableName = String(this.data1?.tableName ?? '');
        if (!tableName) return;
        this.dialog.open(TableHistoryComponent, {
            maxHeight: '90dvh',
            panelClass: 'sai-dialog-lg',
            data: {
                tableName,
                displayName: String(this.form.get('name')?.value || tableName),
                canRevert: !this.isForeignReadOnlyTable,
            },
        });
    }

    /** Persist the agent-write checkbox, only when it changed since the dialog loaded it. */
    private saveAgentWriteIfChanged() {
        const control = this.form.get('allowAgentWrite');
        const current = control?.value;
        if (
            !this.showAgentWriteToggle ||
            control?.disabled ||
            !agentWriteNeedsSave(this.initialAllowAgentWrite, current)
        ) {
            return of(null);
        }
        return this.tableService
            .setTableAgentWrite({
                tableName: this.data1.tableName,
                dataSourceId: this.data1.dataSourceId,
                allowAgentWrite: current === true,
            })
            .pipe(
                tap(() => {
                    this.initialAllowAgentWrite = current === true;
                })
            );
    }

    private updateTableDescription(): void {
        const modal = {
            tableName: this.data1.tableName,
            newName: this.form.get('name')!.value,
            description: this.form.get('description')!.value,
            structure: this.form.get('structure')!.value,
            dataSourceId: this.data1.dataSourceId,
        };
        from(this.unlockPendingStructureColumnsBeforeSave())
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((unlocked) => {
                    if (!unlocked) {
                        return throwError(() => ({
                            error: {
                                message: 'Failed to unlock columns before save.',
                            },
                        }));
                    }
                    return this.tableService.changeTableDecription(modal);
                }),
                switchMap(() => this.saveAgentWriteIfChanged()),
                switchMap(() => from(this.lockPendingStructureColumnsAfterSave()))
            )
            .subscribe({
                next: () => this.dialogRef.close(true),
                error: (e) => {
                    this.loadingOnSubmit = false;
                    this.cdr.markForCheck();
                    this.alert.failedMessage(
                        e?.error?.message ?? 'Update failed'
                    );
                },
            });
    }

    saveExternalTableDescription(): void {
        const tableName = this.data1?.tableName;
        const dataSourceId = this.data1?.dataSourceId;
        if (!tableName || dataSourceId == null || dataSourceId === '') {
            this.alert.failedMessage(
                this.transloco.translate('Missing table or data source')
            );
            return;
        }
        const refetchExternalList = this.isExternalTableDialog();

        this.savingExternalMeta = true;
        this.cdr.markForCheck();
        from(this.unlockPendingStructureColumnsBeforeSave())
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap((unlocked) => {
                    if (!unlocked) {
                        return throwError(() => ({
                            error: {
                                message: 'Failed to unlock columns before save.',
                            },
                        }));
                    }
                    return this.tableService.changeExternalTableDescription({
                        tableName: String(tableName),
                        description: String(
                            this.form.get('description')?.value ?? ''
                        ),
                        structure: String(
                            this.form.get('structure')?.value ?? ''
                        ),
                        dataSourceId: String(dataSourceId),
                    });
                }),
                switchMap(() => from(this.lockPendingStructureColumnsAfterSave())),
                switchMap(() => {
                    if (!refetchExternalList) {
                        return of(null);
                    }
                    return this.datasourceService
                        .getExternalTablesInDatasource(String(dataSourceId))
                        .pipe(catchError(() => of(null)));
                }),
                finalize(() => {
                    this.savingExternalMeta = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (res) => {
                    if (res) {
                        this.applyExternalTableListRefresh(res, String(tableName));
                    }
                    this.savedExternalMetaThisVisit = true;
                    this.snapshotInitialExternalMeta();
                    this.alert.successMessage(
                        this.transloco.translate(
                            'Table description and structure saved successfully'
                        )
                    );
                },
                error: e =>
                    this.alert.failedMessage(
                        e?.error?.message ??
                            this.transloco.translate(
                                'Failed to save table description and structure'
                            )
                    ),
            });
    }

    /** True when this dialog was opened for an external engine table. */
    isExternalTableDialog(): boolean {
        return isExternalTableDialogType(this.data1?.type);
    }

    /**
     * Transloco key for the dialog title: the engine name for external tables,
     * otherwise the dialog type itself (Add / View / ...).
     */
    get dialogTitleKey(): string {
        const engine = externalDatatypeFromDialogType(this.data1?.type);
        if (!engine) {
            return this.data1?.type;
        }
        // The app labels this engine "PostgreSQL" everywhere the user can see it.
        return engine === 'Remote Postgres' ? 'PostgreSQL' : engine;
    }

    private snapshotInitialExternalMeta(): void {
        if (!this.isExternalTableDialog()) return;
        this.initialExternalDescription = String(
            this.form.get('description')?.value ?? ''
        );
        this.initialExternalStructure = String(
            this.form.get('structure')?.value ?? ''
        );
    }

    private isExternalDescriptionStructureDirty(): boolean {
        if (!this.isExternalTableDialog()) return false;
        const d = String(this.form.get('description')?.value ?? '');
        const s = String(this.form.get('structure')?.value ?? '');
        return (
            d !== this.initialExternalDescription ||
            s !== this.initialExternalStructure
        );
    }

    /** Merge row from `external-tables/get-tables/:id` into dialog data and form. */
    private applyExternalTableListRefresh(res: any, tableName: string): void {
        const list = this.datasourceService.externalTablesFromResponse(res);
        if (!Array.isArray(list) || !list.length) return;
        const row = list.find((t: any) => t?.value === tableName);
        if (!row) return;
        if (this.data1?.tableInfo) {
            this.data1.tableInfo = { ...this.data1.tableInfo, ...row };
        }
        this.form.patchValue({
            structure: row.description ?? '',
            description: row.about ?? '',
        });
        this.parseStructureToTable();
        // External API returns KPI definitions under `kpis`, not `kpiLogic`.
        this.metricDefinitions = this.getKpiLogicFromInput();
        this.cdr.markForCheck();
    }

    // ---------------- core logic import ----------------
    openCoreLogicImportGuidelines(): void {
        if (this.isForeignReadOnlyTable) {
            return;
        }

        const ref = this.dialog.open(CoreLogicImportGuidelinesComponent, {
            width: '84%',
            maxHeight: '80dvh',
            panelClass: 'biggDialog',
            disableClose: false,
            hasBackdrop: true,
            backdropClass: 'guidelines-backdrop',
            autoFocus: false,
            restoreFocus: false,
        });

        ref.afterClosed().subscribe((action) => {
            if (action === 'downloadTemplate') {
                this.downloadCoreLogicTemplate();
                this.openCoreLogicImportGuidelines();
                return;
            }
            if (action === 'upload' && this.coreLogicFileInput) {
                this.coreLogicFileInput.nativeElement.value = '';
                this.coreLogicFileInput.nativeElement.click();
            }
        });
    }

    onCoreLogicFileSelected(event: any): void {
        const file: File | undefined = event?.target?.files?.[0];
        if (!file) return;

        const fileName = String(file.name || '').toLowerCase();
        const isCsv = file.type === 'text/csv' || fileName.endsWith('.csv');
        const isExcel =
            file.type ===
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            file.type === 'application/vnd.ms-excel' ||
            fileName.endsWith('.xlsx') ||
            fileName.endsWith('.xls');

        this.loading = true;
        this.currentParseMode = 'coreLogicImport';
        this.importedCoreLogicFileName = file.name || '';
        this.importedCoreLogicFile = file;

        if (isCsv) {
            this.ngxCsvParser
                .parse(file, { header: true, delimiter: ',', encoding: 'utf8' })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (rows: any[] | NgxCSVParserError) => {
                        this.loading = false;
                        if (!Array.isArray(rows) || !rows.length) {
                            this.alert.failedMessage('File has no data rows');
                            this.cdr.markForCheck();
                            return;
                        }
                        if (
                            Object.keys(rows[0]).some(k =>
                                Number.isInteger(Number(k))
                            )
                        ) {
                            this.alert.failedMessage(
                                'Column headers missing in file.'
                            );
                            this.cdr.markForCheck();
                            return;
                        }
                        this.applyImportedCoreLogicRows(rows);
                    },
                    error: () => {
                        this.loading = false;
                        this.alert.failedMessage('CSV parse error');
                        this.cdr.markForCheck();
                    },
                });
            return;
        }

        if (isExcel) {
            if (!this.worker) {
                this.loading = false;
                this.alert.failedMessage('Worker unavailable');
                return;
            }
            this.worker.postMessage({ file });
            return;
        }

        this.loading = false;
        this.alert.failedMessage('Unsupported file type');
        this.cdr.markForCheck();
    }

    private handleCoreLogicParsedFromXlsx(aoa: string[][]): void {
        this.loading = false;
        const headerRow = (aoa?.[0] || []).map(h => String(h || '').trim());
        const bodyRows = aoa?.slice(1) || [];
        if (!headerRow.length || !bodyRows.length) {
            this.alert.failedMessage('File has no data rows');
            this.cdr.markForCheck();
            return;
        }
        if (headerRow.some(h => Number.isInteger(Number(h)))) {
            this.alert.failedMessage('Column headers missing in file.');
            this.cdr.markForCheck();
            return;
        }

        const rowsObj = bodyRows.map(row =>
            headerRow.reduce((acc, key, idx) => {
                acc[key] = row[idx];
                return acc;
            }, {} as Record<string, any>)
        );
        this.applyImportedCoreLogicRows(rowsObj);
    }

    private applyImportedCoreLogicRows(rows: any[]): void {
        const parsed = rows
            .map(row => this.mapImportedCoreLogicRow(row))
            .filter((row): row is KpiLogicItem => !!row);

        if (!parsed.length) {
            this.alert.failedMessage(
                'No valid core logic rows found. Expected columns: Level, KPI Name, Calculation, Description.'
            );
            this.cdr.markForCheck();
            return;
        }

        if (this.metricDefinitionsBeforeImport == null) {
            this.metricDefinitionsBeforeImport = [...this.metricDefinitions];
        }
        this.metricDefinitions = parsed;
        this.coreLogicImportedPendingSave = true;
        this.alert.successMessage(
            `Imported ${parsed.length} core logic row${parsed.length === 1 ? '' : 's'}`
        );
        this.cdr.markForCheck();
    }

    async saveImportedCoreLogic(): Promise<void> {
        if (!this.coreLogicImportedPendingSave || this.isForeignReadOnlyTable) {
            return;
        }

        const isExternal = this.isExternalTableDialog();
        const targetTableId = isExternal
            ? this.kpiAddExternalTableId
            : this.kpiAddInternalTableId;

        if (targetTableId == null) {
            this.alert.failedMessage('Table id is missing.');
            return;
        }
        if (!this.importedCoreLogicFile) {
            this.alert.failedMessage('Please import a core logic file first.');
            return;
        }

        this.isSavingImportedCoreLogic = true;
        this.cdr.markForCheck();

        try {
            const response = await firstValueFrom(
                this.kpiLogicService.importKpis(
                    targetTableId,
                    isExternal,
                    this.importedCoreLogicFile
                )
            );
            const importedKpis = this.extractImportedKpisFromResponse(response);
            if (importedKpis.length) {
                this.metricDefinitions = importedKpis;
                this.syncMetricDefinitionsToDialogData(importedKpis);
            } else {
                this.syncMetricDefinitionsToDialogData(this.metricDefinitions);
            }
            this.coreLogicImportedPendingSave = false;
            this.metricDefinitionsBeforeImport = null;
            this.importedCoreLogicFileName = '';
            this.importedCoreLogicFile = null;
            this.alert.successMessage('Imported core logic saved successfully');
        } catch (e: any) {
            this.alert.failedMessage(this.getRegenerateKpisErrorMessage(e));
        } finally {
            this.isSavingImportedCoreLogic = false;
            this.cdr.markForCheck();
        }
    }

    removeImportedCoreLogic(): void {
        if (!this.coreLogicImportedPendingSave) {
            return;
        }
        this.metricDefinitions = this.metricDefinitionsBeforeImport
            ? [...this.metricDefinitionsBeforeImport]
            : [];
        this.coreLogicImportedPendingSave = false;
        this.metricDefinitionsBeforeImport = null;
        this.importedCoreLogicFileName = '';
        this.importedCoreLogicFile = null;
        this.cdr.markForCheck();
    }

    private mapImportedCoreLogicRow(row: Record<string, any>): KpiLogicItem | null {
        const level = this.readImportedCell(row, ['level', 'kpi_level']);
        const kpiName = this.readImportedCell(row, ['kpi_name', 'kpi name', 'name', 'kpi']);
        const calculation = this.readImportedCell(row, [
            'calculation',
            'formula',
            'expression',
        ]);
        const description = this.readImportedCell(row, [
            'description',
            'desc',
            'about',
        ]);
        const lockedValue = this.readImportedCell(row, [
            'locked',
            'islocked',
            'is_locked',
        ]);

        const hasContent =
            String(level ?? '').trim() ||
            String(kpiName ?? '').trim() ||
            String(calculation ?? '').trim() ||
            String(description ?? '').trim();
        if (!hasContent) {
            return null;
        }

        return {
            level: String(level ?? '').trim() || undefined,
            kpi_name: String(kpiName ?? '').trim(),
            calculation: String(calculation ?? '').trim(),
            description: String(description ?? '').trim(),
            isLocked: this.toBooleanLike(lockedValue),
        };
    }

    private readImportedCell(
        row: Record<string, any>,
        aliases: string[]
    ): any {
        const normalized = new Map<string, any>();
        Object.keys(row || {}).forEach((key) => {
            normalized.set(this.normalizeCoreLogicHeaderKey(key), row[key]);
        });
        for (const alias of aliases) {
            const hit = normalized.get(this.normalizeCoreLogicHeaderKey(alias));
            if (hit !== undefined) {
                return hit;
            }
        }
        return '';
    }

    private normalizeCoreLogicHeaderKey(value: string): string {
        return String(value || '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_+|_+$/g, '');
    }

    private toBooleanLike(value: any): boolean {
        const normalized = String(value ?? '')
            .trim()
            .toLowerCase();
        return ['true', 'yes', '1', 'y', 'locked'].includes(normalized);
    }

    private syncMetricDefinitionsToDialogData(kpis: KpiLogicItem[]): void {
        const tableInfo = this.data1?.tableInfo as any;
        if (tableInfo) {
            if (this.isExternalTableDialog()) {
                tableInfo.kpis = [...kpis];
            } else {
                tableInfo.kpiLogic = [...kpis];
            }
            this.data1.tableInfo = { ...tableInfo };
            return;
        }

        if (this.isExternalTableDialog()) {
            this.data1.kpis = [...kpis];
        } else {
            this.data1.kpiLogic = [...kpis];
        }
    }

    private extractImportedKpisFromResponse(response: any): KpiLogicItem[] {
        const sources = [
            response?.data?.data?.kpiLogic,
            response?.data?.data?.kpis,
            response?.data?.kpiLogic,
            response?.data?.kpis,
            response?.kpiLogic,
            response?.kpis,
        ];
        const list = sources.find(item => Array.isArray(item));
        if (!Array.isArray(list)) {
            return [];
        }

        return list
            .filter(item => item && typeof item === 'object')
            .map((item: any) => ({
                id: item.id ?? item.kpi_id ?? item.kpiId,
                level: item.level ?? item.kpi_level ?? item.kpiLevel ?? undefined,
                kpi_name: String(
                    item.kpi_name ?? item.kpiName ?? item.name ?? ''
                ),
                calculation: String(
                    item.calculation ?? item.formula ?? item.expression ?? ''
                ),
                description: String(
                    item.description ?? item.desc ?? item.about ?? ''
                ),
                isLocked: Boolean(
                    item.isLocked ?? item.locked ?? item.is_locked
                ),
            }))
            .filter(item => item.kpi_name || item.calculation || item.description);
    }

    private downloadCoreLogicTemplate(): void {
        const header = ['Level', 'KPI Name', 'Calculation', 'Description'];
        const csv = [header]
            .map(row => row.map(cell => this.escapeCsvValue(cell)).join(','))
            .join('\n');
        const blob = new Blob(['\uFEFF' + csv], {
            type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'core_logic_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    // ---------------- append flow ----------------
    handleAppendClick(): void {
        if (this.data1?.type !== 'View') return;
        if (this.isForeignReadOnlyTable) return;

        const ref = this.dialog.open(GuidlinesComponent, {
            width: '84%',
            maxHeight: '80dvh',
            panelClass: 'biggDialog',
            disableClose: false,
            hasBackdrop: true,
            backdropClass: 'guidelines-backdrop',
            autoFocus: false,
            restoreFocus: false,
        });

        ref.afterClosed().subscribe((ok) => {
            if (ok && this.appendFileInput) {
                this.appendFileInput.nativeElement.value = '';
                this.appendFileInput.nativeElement.click();
            }
        });
    }

    onAppendFileSelected(event: any): void {
        const file: File | undefined = event?.target?.files?.[0];
        if (!file) return;
        this.fileSizeKB = +(file.size / 1024).toFixed(2);
        this.currentParseMode = 'append';
        this.loading = true;
        this.isAppendFileLoading = true;

        if (file.type === 'text/csv') {
            this.ngxCsvParser
                .parse(file, { header: true, delimiter: ',', encoding: 'utf8' })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: (rows: any[] | NgxCSVParserError) => {
                        if (!Array.isArray(rows) || !rows.length) {
                            this.loading = false;
                            this.isAppendFileLoading = false;
                            this.cdr.markForCheck();
                            return;
                        }
                        if (
                            Object.keys(rows[0]).some(k =>
                                Number.isInteger(Number(k))
                            )
                        ) {
                            this.loading = false;
                            this.isAppendFileLoading = false;
                            this.alert.failedMessage(
                                'Column headers missing in file.'
                            );
                            this.cdr.markForCheck();
                            return;
                        }

                        const processedRows =
                            this.fileService.processCSVDataForAccountingFormat(
                                rows
                            );
                        this.handleAppendParsedFromCsv(processedRows);
                        // handleAppendParsedFromCsv eventually flips loading in success/error
                        this.cdr.markForCheck();
                    },
                    error: (_e: NgxCSVParserError) => {
                        this.loading = false;
                        this.isAppendFileLoading = false;
                        this.alert.failedMessage('CSV parse error');
                        this.cdr.markForCheck();
                    },
                });
            return;
        }

        if (
            file.type ===
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ) {
            if (!this.worker) {
                this.loading = false;
                this.isAppendFileLoading = false;
                this.alert.failedMessage('Worker unavailable');
                return;
            }
            this.worker.postMessage({ file });
            return;
        }

        this.loading = false;
        this.isAppendFileLoading = false;
        this.alert.failedMessage('Unsupported file type');
    }

    private handleAppendParsedFromCsv(rowsObj: any[]): void {
        const { headers, rows } =
            this.fileService.rowsFromCsvObjects(rowsObj);
        this.processAppendCandidate(headers, rows);
    }

    private handleAppendParsedFromXlsx(aoa: string[][]): void {
        const { headers, rows } = this.fileService.rowsFromXlsxAoA(aoa);
        this.processAppendCandidate(headers, rows);
    }

    private processAppendCandidate(
        newHeaders: string[],
        newRows: any[][]
    ): void {
        if (!this.originalHeadersClean.length) this.captureOriginalSchemaOnce();

        const mismatch = this.fileService.validateAppendAgainstOriginal({
            originalHeaders: this.originalHeaders,
            originalHeadersClean: this.originalHeadersClean,
            originalTypes: this.originalTypes,
            apiSchema: this.apiSchema,
            apiComments: this.effectiveApiComments,
            newHeaders,
            newRows,
            units: (this.form.get('units') as FormArray).value,
        });

        if (!mismatch.ok) {
            this.loading = false;
            this.isAppendFileLoading = false;
            this.resetAppendSelection();

            let errorDetails = '';
            if (!mismatch.columnsOk) {
                errorDetails += `<div style="margin-bottom: 0.5rem;"><strong>Column Mismatch:</strong> ${mismatch.columnsMsg}</div>`;
            }
            if (!mismatch.typesOk) {
                errorDetails += `<div style="margin-bottom: 0.5rem;"><strong>Data Type Mismatch:</strong> ${mismatch.typesMsg}</div>`;
            }

            this.openInfoDialog(
                'Data Structure Does Not Match',
                `
      <div style="line-height:1.6">
      <div style="margin-bottom: 1rem; color: #d32f2f; font-weight: bold;">
        The new data structure does not match the existing table structure.
      </div>
      ${errorDetails}
      <div style="margin-top: 1rem; padding-top: 1rem; border-top: 1px solid #e0e0e0;">
        <em>No changes were applied. Please fix your file to match the existing data structure and try again.</em>
      </div>
      </div>
    `
            );

            return;
        }

        this.isTableUploadActive = true;
        this.cdr.markForCheck();
        this.appendCombineAndUpload(mismatch.alignedRows)
            .then(() => this.extractTableStructure())
            .then(() => {
                this.loading = false;
                this.isAppendFileLoading = false;
                this.loadingOnSubmit = false;
                this.isTableUploadActive = false;
                this.showProgress = false;
                this.isFinalizing = false;
                this.isAppending = false;
                this.dialogRef.close(true);
                this.cdr.markForCheck();
            })
            .catch((err) => {
                this.loading = false;
                this.isAppendFileLoading = false;
                this.loadingOnSubmit = false;
                this.isTableUploadActive = false;
                this.showProgress = false;
                this.isFinalizing = false;
                this.handleUploadError(err);
                this.cdr.markForCheck();
            });
    }

    private resetAppendSelection(): void {
        if (this.appendFileInput?.nativeElement) {
            this.appendFileInput.nativeElement.value = '';
        }
    }

    appendNow(): void {
        const mismatch = this.fileService.validateAppendAgainstOriginal({
            originalHeaders: this.originalHeaders,
            originalHeadersClean: this.originalHeadersClean,
            originalTypes: this.originalTypes,
            apiSchema: this.apiSchema,
            apiComments: this.effectiveApiComments,
            newHeaders: this.headers,
            newRows: this.allRows,
            units: (this.form.get('units') as FormArray).value,
        });

        if (!mismatch.ok) {
            this.alert.failedMessage(
                `Still not matching.\nColumns: ${mismatch.columnsMsg}\nTypes: ${mismatch.typesMsg || 'OK'
                }`
            );
            return;
        }

        this.loadingOnSubmit = true;
        this.isTableUploadActive = true;
        this.progressValue = 0;
        this.uploadStatus = '';
        this.showProgress = false;
        this.isFinalizing = false;
        this.cdr.markForCheck();
        this.appendCombineAndUpload(mismatch.alignedRows)
            .then(() => this.extractTableStructure())
            .then(() => {
                this.loadingOnSubmit = false;
                this.isTableUploadActive = false;
                this.loading = false;
                this.showProgress = false;
                this.isFinalizing = false;
                this.isAppending = false;
                this.dialogRef.close(true);
                this.cdr.markForCheck();
            })
            .catch((err) => {
                this.loadingOnSubmit = false;
                this.isTableUploadActive = false;
                this.loading = false;
                this.showProgress = false;
                this.isFinalizing = false;
                this.isAppending = false;
                this.handleUploadError(err);
                this.cdr.markForCheck();
            });
    }

    private async appendCombineAndUpload(newRowsAA: any[][]): Promise<void> {
        const dateColumns = this.fileService.buildAppendDateColumns({
            originalHeaders: this.originalHeaders,
            originalHeadersClean: this.originalHeadersClean,
            originalTypes: this.originalTypes,
            apiSchema: this.apiSchema,
            apiComments: this.effectiveApiComments,
        });

        const payload: UploadPayload = {
            name: this.form.get('name')!.value,
            description: this.form.get('description')!.value,
            dataSourceId: this.data1?.dataSourceId,
            table: Object.fromEntries(
                this.originalHeaders.map((h, i) => [
                    this.fileService.cleanHeaderKey(h),
                    this.originalTypes[i],
                ])
            ),
            comments: this.fileService.buildAppendComments(
                this.originalHeaders,
                this.effectiveApiComments
            ),
            dateColumns,
            append: true,
            data: this.fileService.mapRowsToObjects(
                this.originalHeaders,
                newRowsAA
            ),
        };


        const fd = this.uploadService.compressToFormData(
            payload,
            this.fileSizeKB
        );

        await this.uploadService.upload(fd, {
            onProgress: (v) => {
                this.showProgress = true;
                this.progressValue = v;
                this.cdr.markForCheck();
            },
            onStatusChange: (status) => {
                this.uploadStatus = status;
                this.cdr.markForCheck();
            },
            onFinalizingChange: (f) => {
                this.isFinalizing = f;
                this.cdr.markForCheck();
            },
            onDone: () => {
                this.showProgress = false;
                this.isFinalizing = false;
                this.uploadStatus = '';
                this.isTableUploadActive = false;
                this.loadingOnSubmit = false;
                this.cdr.markForCheck();
            },
            onError: () => {
                this.showProgress = false;
                this.isFinalizing = false;
                this.uploadStatus = '';
                this.isTableUploadActive = false;
                this.loadingOnSubmit = false;
                this.cdr.markForCheck();
            },
        });

        this.alert.successMessage('New rows uploaded successfully!');
    }

    // ---------------- structure / description ----------------
    extractTableLoader: boolean = false;

    /**
     * Technical table name for `generate-table-desc` / pending-job keys - aligned with
     * {@link TableStructureBackgroundService} and tables list `isExternalTableDescriptionPending`.
     */
    private resolveInternalTableNameForExtraction(): string {
        return String(
            this.data1?.tableName ??
                this.data1?.tableInfo?.name ??
                this.form.get('name')?.value ??
                ''
        ).trim();
    }

    private resumePendingTableExtractionIfAny(): void {
        const tableName = this.resolveInternalTableNameForExtraction();
        const dataSourceId = this.data1?.dataSourceId;
        if (!tableName || dataSourceId == null || dataSourceId === '') {
            return;
        }
        const isPending =
            this.tableStructureBackgroundService.isExtractionPending({
                tableName,
                dataSourceId,
            });
        if (!isPending) {
            return;
        }

        this.extractTableLoader = true;
        this.cdr.markForCheck();
        this.tableStructureBackgroundService.syncPendingExtractionState();
        void this.waitForPendingTableExtraction(tableName, dataSourceId);
    }

    private async waitForPendingTableExtraction(
        tableName: string,
        dataSourceId: string | number
    ): Promise<void> {
        try {
            await this.tableStructureBackgroundService.waitForCompletion({
                tableName,
                dataSourceId,
            });

            if (!this.isDestroyed) {
                await this.refreshStructureAndDescriptionFromServer(
                    tableName,
                    dataSourceId
                );
            }
        } catch {
            // Errors are surfaced by socket/background service alerts.
        } finally {
            if (!this.isDestroyed) {
                this.extractTableLoader = false;
                this.cdr.markForCheck();
            }
        }
    }

    async extractTableStructure(): Promise<void> {
        if (this.isForeignReadOnlyTable) {
            return;
        }
        const tableName = this.resolveInternalTableNameForExtraction();
        const dataSourceId = this.data1?.dataSourceId;
        if (!tableName || dataSourceId == null || dataSourceId === '') {
            this.alert.failedMessage('Missing table name or data source id');
            return;
        }

        const modal = {
            tableName,
            threshold: 10,
            dataSourceId,
        };

        try {
            this.extractTableLoader = true;
            this.cdr.markForCheck();
            const sourceType = externalDatatypeFromDialogType(this.data1?.type);
            await this.tableStructureBackgroundService.triggerExtraction({
                ...modal,
                ...(sourceType ? { sourceType } : {}),
            });

            this.alert.successMessage(
                'Table structure extraction started in the background.'
            );
            if (!this.isDestroyed) {
                this.dialogRef.close({
                    tableStructureExtractionStarted: true,
                    tableName,
                    dataSourceId,
                });
            }
        } catch (e: any) {
            this.alert.failedMessage(
                e?.error?.message || 'Failed to extract structure'
            );
        } finally {
            if (!this.isDestroyed) {
                this.extractTableLoader = false;
                this.cdr.markForCheck();
            }
        }
    }

    private async refreshStructureAndDescriptionFromServer(
        tableName: string,
        dataSourceId: string | number
    ): Promise<void> {
        if (this.isExternalTableDialog()) {
            const ds = String(dataSourceId);
            const externalTableRes: any = await firstValueFrom(
                this.tableService
                    .getExternalTableData(
                        tableName,
                        this.pageIndex,
                        this.pageSize,
                        ds
                    )
                    .pipe(take(1))
            );
            const metaRes: any = await firstValueFrom(
                this.tableService
                    .getExternalTableMeta(tableName, ds)
                    .pipe(take(1), catchError(() => of(null)))
            );
            this.hydrateServerPage(externalTableRes);
            this.applyTableMetaResponse(metaRes);

            const res: any = await firstValueFrom(
                this.datasourceService
                    .getExternalTablesInDatasource(String(dataSourceId))
                    .pipe(take(1))
            );
            this.applyExternalTableListRefresh(res, tableName);
            this.applyTableMetaResponse(metaRes);
            return;
        }

        const [tableDataRes, metaRes]: [any, any] = await Promise.all([
            firstValueFrom(
                this.tableService
                    .getTableByName(
                        tableName,
                        this.pageIndex,
                        this.pageSize
                    )
                    .pipe(take(1))
            ),
            firstValueFrom(
                this.tableService
                    .getTableMeta(tableName)
                    .pipe(take(1), catchError(() => of(null)))
            ),
        ]);
        this.hydrateServerPage(tableDataRes);
        this.applyTableMetaResponse(metaRes);
    }

    toggleStructureView(): void {
        this.structureViewMode =
            this.structureViewMode === 'text' ? 'table' : 'text';
        if (this.structureViewMode === 'table') {
            this.parseStructureToTable();
        }
    }

    private getTableStructureSection(
        json: Record<string, unknown> | null | undefined
    ): Record<string, unknown> | undefined {
        if (!json || typeof json !== 'object') {
            return undefined;
        }
        const nested = json['table_structure'];
        if (nested && typeof nested === 'object') {
            const n = nested as Record<string, unknown>;
            if (
                n['metric_columns_structure'] ||
                n['dimension_columns_structure']
            ) {
                return n;
            }
        }
        if (
            json['metric_columns_structure'] ||
            json['dimension_columns_structure']
        ) {
            return json;
        }
        if (nested && typeof nested === 'object') {
            return nested as Record<string, unknown>;
        }
        return undefined;
    }

    parseStructureToTable(): void {
        const structureText = this.form.get('structure')?.value || '';
        this.structureJsonRoot = null;
        this.statisticsScope = null;
        if (!structureText.trim()) {
            this.parsedStructure = [];
            this.structureTextViewValue = '';
            this.structureColumnKeys = [];
            this.structureDescEditStates = [];
            this.hasUniqueColumn = false;
            this.refreshGrainDisplay();
            return;
        }

        const columns: any[] = [];

        try {
            const jsonData = JSON.parse(structureText);
            // This dialog carries its own copy of the column walk, but the scope marker is read
            // through the shared util so the two copies cannot disagree about what the figures
            // describe. Do not inline a second reader here.
            this.statisticsScope = readTableStatisticsScope(jsonData);

            const tableStructure = this.getTableStructureSection(
                jsonData as Record<string, unknown>
            );
            const metricStructure = tableStructure?.[
                'metric_columns_structure'
            ] as Record<string, unknown> | undefined;
            const dimensionStructure = tableStructure?.[
                'dimension_columns_structure'
            ] as Record<string, unknown> | undefined;

            if (metricStructure || dimensionStructure) {
                this.structureJsonRoot = jsonData as Record<string, unknown>;

                const normalizeColumn = (
                    columnName: string,
                    keys: string[],
                    values: unknown[]
                ): any => {
                    const keyValueMap = new Map<string, unknown>();
                    keys.forEach((key, idx) => keyValueMap.set(key, values[idx]));

                    const sampleValuesRaw = keyValueMap.get('sample_values');
                    const minMaxRaw = keyValueMap.get('min_max');
                    const sampleValues = Array.isArray(sampleValuesRaw)
                        ? sampleValuesRaw
                              .map(value => String(value ?? '').trim())
                              .filter(Boolean)
                              .join(', ')
                        : '';

                    let minDate = '';
                    let maxDate = '';
                    const type = String(
                        keyValueMap.get('data_type') ?? ''
                    ).trim();
                    if (
                        Array.isArray(minMaxRaw) &&
                        minMaxRaw.length === 2
                    ) {
                        if (
                            type.toUpperCase() === 'DATE' ||
                            type.toUpperCase().includes('TIMESTAMP')
                        ) {
                            minDate =
                                minMaxRaw[0] != null
                                    ? String(minMaxRaw[0]).trim()
                                    : '';
                            maxDate =
                                minMaxRaw[1] != null
                                    ? String(minMaxRaw[1]).trim()
                                    : '';
                        }
                    }

                    return {
                        name: columnName,
                        keyValueMap: Object.fromEntries(keyValueMap.entries()),
                        type,
                        description: String(
                            keyValueMap.get('description') ?? ''
                        ).trim(),
                        nullCount:
                            keyValueMap.get('null_count') != null
                                ? String(keyValueMap.get('null_count'))
                                : '',
                        distinctValues:
                            keyValueMap.get('distinct_count') != null
                                ? String(keyValueMap.get('distinct_count'))
                                : '',
                        sampleValues,
                        minDate,
                        maxDate,
                        unique: keyValueMap.get('is_unique'),
                    };
                };

                const collectColumns = (
                    section: Record<string, unknown> | undefined,
                    semanticType: 'dimension' | 'metric'
                ): { columns: any[]; keys: string[] } => {
                    if (!section || typeof section !== 'object') {
                        return { columns: [], keys: [] };
                    }
                    const keys = Array.isArray(section['K'])
                        ? (section['K'] as unknown[]).map(k => String(k))
                        : [];
                    const cols = section['columns'] as
                        | Record<string, unknown>
                        | undefined;
                    if (!cols || typeof cols !== 'object') {
                        return { columns: [], keys };
                    }

                    return {
                        keys,
                        columns: Object.entries(cols)
                            .filter(([, info]) => Array.isArray(info))
                            .map(([columnName, info]) => ({
                                ...normalizeColumn(
                                    columnName,
                                    keys,
                                    info as unknown[]
                                ),
                                semanticType,
                            })),
                    };
                };

                const dimData = collectColumns(
                    dimensionStructure,
                    'dimension'
                );
                const metricData = collectColumns(metricStructure, 'metric');
                const parsedColumns = [...dimData.columns, ...metricData.columns];
                const orderedKeys = [
                    'data_type',
                    'description',
                    'null_count',
                    'distinct_count',
                    'sample_values',
                    'min_max',
                    'aggregation_type',
                    'is_unique',
                    'unit',
                    'unit_ref_column',
                ];
                const discoveredKeys = [
                    ...new Set([...dimData.keys, ...metricData.keys]),
                ];
                this.structureColumnKeys = [
                    ...orderedKeys.filter(key => discoveredKeys.includes(key)),
                    ...discoveredKeys.filter(key => !orderedKeys.includes(key)),
                ];

                this.parsedStructure = parsedColumns;
                this.structureTextViewValue =
                    this.buildStructureTextFromParsed(parsedColumns);
                this.structureDescEditStates = parsedColumns.map(() => false);
                this.hasUniqueColumn = this.structureColumnKeys.includes('is_unique');
                this.refreshGrainDisplay();
                return;
            }

            if (jsonData.Columns && typeof jsonData.Columns === 'object') {
                this.structureJsonRoot = jsonData as Record<string, unknown>;
                const columnsData = jsonData.Columns;

                for (const [columnName, columnInfo] of Object.entries(
                    columnsData
                )) {
                    if (Array.isArray(columnInfo) && columnInfo.length >= 6) {
                        const columnData: any = {
                            name: columnName,
                            type: columnInfo[0] || '',
                            description: columnInfo[1] || '',
                            nullCount:
                                columnInfo[2] !== null
                                    ? String(columnInfo[2])
                                    : '',
                            distinctValues:
                                columnInfo[3] !== null
                                    ? String(columnInfo[3])
                                    : '',
                            sampleValues: '',
                            minDate: '',
                            maxDate: '',
                        };
                        if (columnInfo.length >= 7) {
                            columnData.unique =
                                columnInfo[6] !== null &&
                                columnInfo[6] !== undefined
                                    ? String(columnInfo[6]).trim()
                                    : '';
                        }

                        const minMax = columnInfo[4];
                        if (
                            Array.isArray(minMax) &&
                            minMax.length === 2
                        ) {
                            if (
                                columnData.type.toUpperCase() === 'DATE' ||
                                columnData.type
                                    .toUpperCase()
                                    .includes('TIMESTAMP')
                            ) {
                                columnData.minDate =
                                    minMax[0] !== null
                                        ? String(minMax[0])
                                        : '';
                                columnData.maxDate =
                                    minMax[1] !== null
                                        ? String(minMax[1])
                                        : '';
                            } else {
                                columnData.sampleValues = `Min: ${minMax[0]}, Max: ${minMax[1]}`;
                            }
                        }

                        const sampleValues = columnInfo[5];
                        if (
                            Array.isArray(sampleValues) &&
                            sampleValues.length > 0
                        ) {
                            if (columnData.sampleValues) {
                                columnData.sampleValues +=
                                    ' | Samples: ' +
                                    sampleValues.join(', ');
                            } else {
                                columnData.sampleValues =
                                    sampleValues.join(', ');
                            }
                        }

                        columns.push(columnData);
                    }
                }

                this.parsedStructure = columns;
                this.structureTextViewValue =
                    this.buildStructureTextFromParsed(columns);
                this.structureColumnKeys = [
                    'data_type',
                    'description',
                    'null_count',
                    'distinct_count',
                    'sample_values',
                    'is_unique',
                ];
                this.structureDescEditStates = columns.map(() => false);
                this.hasUniqueColumn = columns.some(col =>
                    Object.prototype.hasOwnProperty.call(col, 'unique')
                );
                this.refreshGrainDisplay();
                return;
            }
        } catch {
        }

        const columnBlocks = structureText.split(/\n\s*\n+/);

        for (const block of columnBlocks) {
            if (!block.trim()) continue;

            const lines = block
                .split('\n')
                .map((l: string) => l.trim())
                .filter((l: string) => !!l);
            if (!lines.length) continue;

            const columnData: any = {
                name: '',
                type: '',
                description: '',
                distinctValues: '',
                nullCount: '',
                sampleValues: '',
                minDate: '',
                maxDate: '',
            };

            for (const line of lines) {
                const columnMatch = line.match(
                    /^Column:\s*(\w+)\s*\(([^)]+)\)/i
                );
                if (columnMatch) {
                    columnData.name = columnMatch[1];
                    columnData.type = columnMatch[2];
                    continue;
                }

                if (line.startsWith('- Description:')) {
                    columnData.description = line
                        .replace('- Description:', '')
                        .trim();
                    continue;
                }

                const distinctMatch = line.match(/- Distinct Values:\s*(.+)/i);
                if (distinctMatch) {
                    columnData.distinctValues = distinctMatch[1].trim();
                    continue;
                }

                const nullMatch = line.match(/- Null Count:\s*(.+)/i);
                if (nullMatch) {
                    columnData.nullCount = nullMatch[1].trim();
                    continue;
                }

                if (line.startsWith('- Sample Values:')) {
                    columnData.sampleValues = line
                        .replace('- Sample Values:', '')
                        .trim();
                    continue;
                }

                if (line.startsWith('- Minimum Date')) {
                    columnData.minDate = line
                        .replace(/- Minimum Date\s*:\s*/i, '')
                        .trim();
                    continue;
                }

                if (line.startsWith('- Maximum Date')) {
                    columnData.maxDate = line
                        .replace(/- Maximum Date\s*:\s*/i, '')
                        .trim();
                    continue;
                }

                const uniqueMatch = line.match(/- Unique:\s*(.+)/i);
                if (uniqueMatch) {
                    columnData.unique = uniqueMatch[1].trim();
                    continue;
                }

                if (
                    line.startsWith('- Distinct Values:') &&
                    !line.includes('distinct values')
                ) {
                    columnData.sampleValues = line
                        .replace('- Distinct Values:', '')
                        .trim();
                }
            }

            if (columnData.name) columns.push(columnData);
        }

        this.parsedStructure = columns;
        this.structureTextViewValue = this.buildStructureTextFromParsed(columns);
        this.structureColumnKeys = [
            'data_type',
            'description',
            'null_count',
            'distinct_count',
            'sample_values',
            'is_unique',
        ];
        this.structureDescEditStates = columns.map(() => false);
        this.hasUniqueColumn = columns.some(col =>
            Object.prototype.hasOwnProperty.call(col, 'unique')
        );
        this.refreshGrainDisplay();
    }

    /**
     * Reads `grain` from parsed structure JSON (root or `table_structure`) or from
     * `data1.tableInfo.grain` when the API returns it alongside / without structure text.
     */
    private refreshGrainDisplay(): void {
        let grainObj: Record<string, unknown> | null = null;
        let tableTypeRaw: unknown;
        let tableShapeRaw: unknown;
        const structureText = String(this.form.get('structure')?.value ?? '').trim();
        if (structureText) {
            try {
                const jsonData = JSON.parse(structureText) as Record<string, unknown>;
                grainObj = this.getGrainObjectFromRoot(jsonData) ?? null;
                tableTypeRaw = this.getTableTypeFromRoot(jsonData);
                tableShapeRaw = this.getTableShapeFromRoot(jsonData);
            } catch {
                // non-JSON structure text
            }
        }
        if (
            !grainObj &&
            this.data1?.tableInfo?.grain &&
            typeof this.data1.tableInfo.grain === 'object' &&
            !Array.isArray(this.data1.tableInfo.grain)
        ) {
            grainObj = this.data1.tableInfo.grain as Record<string, unknown>;
        }
        if (tableTypeRaw === undefined || tableTypeRaw === null) {
            tableTypeRaw =
                this.data1?.tableInfo?.table_type ??
                this.data1?.tableInfo?.tableType ??
                this.data1?.table?.table_type ??
                this.data1?.table?.tableType;
        }
        if (tableShapeRaw === undefined || tableShapeRaw === null) {
            tableShapeRaw =
                this.data1?.tableInfo?.table_shape ??
                this.data1?.tableInfo?.tableShape ??
                this.data1?.table?.table_shape ??
                this.data1?.table?.tableShape;
        }
        this.tableTypeDisplay = this.formatTableTypeLabel(tableTypeRaw);
        this.tableShapeColumns = this.buildTableShapeColumns(tableShapeRaw);
        if (grainObj) {
            this.grainTemporalCoverageColumns =
                this.buildTemporalCoverageColumns(grainObj);
            this.parsedGrainRows = this.buildGrainDisplayRows(grainObj);
        } else {
            this.grainTemporalCoverageColumns = [];
            this.parsedGrainRows = [];
        }
    }

    private getTableTypeFromRoot(root: Record<string, unknown>): unknown {
        if (root['table_type'] !== undefined && root['table_type'] !== null) {
            return root['table_type'];
        }
        const ts = root['table_structure'];
        if (ts && typeof ts === 'object' && !Array.isArray(ts)) {
            const nestedTableType = (ts as Record<string, unknown>)['table_type'];
            if (nestedTableType !== undefined && nestedTableType !== null) {
                return nestedTableType;
            }
        }
        return undefined;
    }

    private getTableShapeFromRoot(root: Record<string, unknown>): unknown {
        if (root['table_shape'] !== undefined && root['table_shape'] !== null) {
            return root['table_shape'];
        }
        const ts = root['table_structure'];
        if (ts && typeof ts === 'object' && !Array.isArray(ts)) {
            const nestedTableShape = (ts as Record<string, unknown>)['table_shape'];
            if (nestedTableShape !== undefined && nestedTableShape !== null) {
                return nestedTableShape;
            }
        }
        return undefined;
    }

    private buildTableShapeColumns(shapeRaw: unknown): GrainTemporalCoverageColumn[] {
        if (!shapeRaw || typeof shapeRaw !== 'object' || Array.isArray(shapeRaw)) {
            return [];
        }
        const shape = shapeRaw as Record<string, unknown>;
        const cols: GrainTemporalCoverageColumn[] = [];
        const rowRaw =
            shape['row'] ??
            shape['rows'] ??
            shape['row_count'] ??
            shape['rowCount'];
        const colRaw =
            shape['columns'] ??
            shape['column'] ??
            shape['column_count'] ??
            shape['columns_count'] ??
            shape['columnCount'];
        const rowCount = this.formatTableShapeCount(rowRaw);
        const colCount = this.formatTableShapeCount(colRaw);

        if (rowCount.trim()) {
            cols.push({ label: 'Rows', value: rowCount });
        }
        if (colCount.trim()) {
            cols.push({ label: 'Columns', value: colCount });
        }
        return cols;
    }

    private formatTableShapeCount(value: unknown): string {
        if (value === undefined || value === null) return '';
        if (typeof value === 'number' && Number.isFinite(value)) {
            return new Intl.NumberFormat('en-US').format(value);
        }
        const raw = String(value).trim();
        if (!raw) return '';
        const numeric = Number(raw.replace(/,/g, ''));
        if (Number.isFinite(numeric) && /^\d+(\.\d+)?$/.test(raw.replace(/,/g, ''))) {
            return new Intl.NumberFormat('en-US').format(numeric);
        }
        return raw;
    }

    private formatTableTypeLabel(value: unknown): string {
        const raw = String(value ?? '').trim();
        if (!raw) return '';
        return raw
            .replace(/[_-]+/g, ' ')
            .replace(/\s+/g, ' ')
            .replace(/\b\w/g, ch => ch.toUpperCase());
    }

    /**
     * Builds header/value pairs for `temporal_coverage` (single data row in the UI).
     */
    private buildTemporalCoverageColumns(
        grain: Record<string, unknown>
    ): GrainTemporalCoverageColumn[] {
        const temporal = grain['temporal_coverage'];
        if (!temporal || typeof temporal !== 'object' || Array.isArray(temporal)) {
            return [];
        }
        const tc = temporal as Record<string, unknown>;
        const nestedLabels: Record<string, string> = {
            type: 'Coverage type',
            min_date: 'Minimum date',
            max_date: 'Maximum date',
            start_date: 'Start date',
            end_date: 'End date',
        };
        const cols: GrainTemporalCoverageColumn[] = [];
        for (const [k, v] of Object.entries(tc)) {
            const s = this.stringifyGrainValue(v);
            if (!s.trim()) continue;
            const label = nestedLabels[k] ?? this.titleCaseGrainField(k);
            cols.push({ label, value: s });
        }
        return cols;
    }

    private getGrainObjectFromRoot(
        root: Record<string, unknown>
    ): Record<string, unknown> | undefined {
        const direct = root['grain'];
        if (direct && typeof direct === 'object' && !Array.isArray(direct)) {
            return direct as Record<string, unknown>;
        }
        const ts = root['table_structure'];
        if (ts && typeof ts === 'object' && !Array.isArray(ts)) {
            const nested = (ts as Record<string, unknown>)['grain'];
            if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
                return nested as Record<string, unknown>;
            }
        }
        return undefined;
    }

    private buildGrainDisplayRows(grain: Record<string, unknown>): GrainDisplayRow[] {
        const rows: GrainDisplayRow[] = [];
        const add = (label: string, value: string, nested?: boolean): void => {
            const v = value.trim();
            if (v) rows.push({ label, value: v, nested });
        };

        const grainKey = grain['grain_key'];
        if (grainKey !== undefined) {
            add('Grain key', this.stringifyGrainValue(grainKey));
        }

        const primaryTime = grain['primary_time_key'];
        if (primaryTime !== undefined && primaryTime !== null) {
            add('Primary time key', String(primaryTime));
        }

        const timeGrain = grain['time_grain'];
        if (timeGrain !== undefined && timeGrain !== null) {
            add('Time grain', String(timeGrain));
        }

        const consumed = new Set([
            'grain_key',
            'primary_time_key',
            'time_grain',
            'temporal_coverage',
        ]);
        for (const key of Object.keys(grain)) {
            if (consumed.has(key)) continue;
            const val = grain[key];
            if (val !== undefined && val !== null) {
                if (typeof val === 'object' && !Array.isArray(val)) {
                    for (const [nk, nv] of Object.entries(val as Record<string, unknown>)) {
                        const s = this.stringifyGrainValue(nv);
                        if (!s.trim()) continue;
                        add(
                            `${this.titleCaseGrainField(key)} - ${this.titleCaseGrainField(nk)}`,
                            s,
                            true
                        );
                    }
                } else {
                    add(this.titleCaseGrainField(key), this.stringifyGrainValue(val));
                }
            }
        }

        return rows;
    }

    private stringifyGrainValue(value: unknown): string {
        if (value == null) return '';
        if (Array.isArray(value)) {
            return value
                .map(v => String(v ?? '').trim())
                .filter(Boolean)
                .join(', ');
        }
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value);
            } catch {
                return String(value);
            }
        }
        return String(value).trim();
    }

    private titleCaseGrainField(key: string): string {
        return key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, ch => ch.toUpperCase());
    }

    startStructureDescEdit(i: number): void {
        if (this.isForeignReadOnlyTable) {
            return;
        }
        const col = this.parsedStructure[i];
        if (!col || this.isStructureColumnLocked(col)) {
            return;
        }
        this.structureDescEditStates[i] = true;
        setTimeout(() => {
            this.structureDescSpans?.get(i)?.nativeElement?.focus();
            this.cdr.markForCheck();
        }, 0);
        this.cdr.markForCheck();
    }

    finishStructureDescEdit(i: number): void {
        if (!this.structureDescEditStates[i]) return;
        const col = this.parsedStructure[i];
        if (!col) {
            this.structureDescEditStates[i] = false;
            this.cdr.markForCheck();
            return;
        }

        const el = this.structureDescSpans?.get(i)?.nativeElement;
        if (el) {
            this.commitStructureDescription(i, el.innerText.trim());
        }
        this.structureDescEditStates[i] = false;
        this.cdr.markForCheck();
    }

    private commitStructureDescription(index: number, text: string): boolean {
        const col = this.parsedStructure[index];
        if (!col) return false;
        if (this.isStructureColumnLocked(col)) return false;
        const nextDescription = text ?? '';
        const prevDescription = String(col.description ?? '');
        if (prevDescription === nextDescription) return false;

        col.description = nextDescription;
        this.syncStructureControlFromParsed();
        const columnName = String(col.name ?? '').trim();
        if (columnName) {
            this.pendingLockStructureColumns.add(columnName);
        }
        this.cdr.markForCheck();
        return true;
    }

    isStructureColumnLocked(col: any): boolean {
        const name = String(col?.name ?? '').trim();
        return !!name && this.lockedStructureColumns.has(name);
    }

    isStructureColumnLocking(col: any): boolean {
        const name = String(col?.name ?? '').trim();
        return !!name && this.lockingStructureColumnName === name;
    }

    private getStructureSourceType(): ExternalDatatype | undefined {
        return externalDatatypeFromDialogType(this.data1?.type);
    }

    private async setStructureColumnLock(
        columnName: string,
        locked: boolean
    ): Promise<boolean> {
        const tableName = String(
            this.form.get('name')?.value ?? this.data1?.tableName ?? ''
        ).trim();
        const dataSourceId = this.data1?.dataSourceId;
        if (!columnName || !tableName || dataSourceId == null || dataSourceId === '') {
            return false;
        }

        this.lockingStructureColumnName = columnName;
        this.cdr.markForCheck();
        try {
            const sourceType = this.getStructureSourceType();
            await firstValueFrom(
                this.tableService.setColumnLock({
                    tableName,
                    dataSourceId,
                    columnName,
                    locked,
                    ...(sourceType ? { sourceType } : {}),
                })
            );
            if (locked) {
                this.lockedStructureColumns.add(columnName);
                this.pendingLockStructureColumns.delete(columnName);
                this.pendingUnlockStructureColumns.delete(columnName);
                const index = this.parsedStructure.findIndex(
                    item => String(item?.name ?? '').trim() === columnName
                );
                if (index >= 0) {
                    this.structureDescEditStates[index] = false;
                }
            } else {
                this.lockedStructureColumns.delete(columnName);
                this.pendingUnlockStructureColumns.delete(columnName);
            }
            this.patchLockedColumnsInDialogData();
            return true;
        } catch (e: any) {
            this.alert.failedMessage(
                e?.error?.message ?? 'Failed to update column lock.'
            );
            return false;
        } finally {
            this.lockingStructureColumnName = null;
            this.cdr.markForCheck();
        }
    }

    private async unlockPendingStructureColumnsBeforeSave(): Promise<boolean> {
        if (
            this.isForeignReadOnlyTable ||
            this.isExternalManagerStaffReadOnly
        ) {
            return true;
        }

        const columnsToUnlock = [...this.pendingUnlockStructureColumns];
        if (!columnsToUnlock.length) {
            return true;
        }

        for (const columnName of columnsToUnlock) {
            const ok = await this.setStructureColumnLock(columnName, false);
            if (!ok) {
                return false;
            }
            this.initialLockedStructureColumns.delete(columnName);
        }
        return true;
    }

    private async lockPendingStructureColumnsAfterSave(): Promise<void> {
        if (
            this.isForeignReadOnlyTable ||
            this.isExternalManagerStaffReadOnly
        ) {
            return;
        }

        const columnsToLock = [...this.pendingLockStructureColumns].filter(
            name => !this.initialLockedStructureColumns.has(name)
        );
        if (!columnsToLock.length) {
            return;
        }

        for (const columnName of columnsToLock) {
            await this.setStructureColumnLock(columnName, true);
        }

        this.initialLockedStructureColumns = new Set(this.lockedStructureColumns);
        this.pendingLockStructureColumns.clear();
    }

    toggleStructureColumnLock(col: any): void {
        if (
            this.isForeignReadOnlyTable ||
            this.isExternalManagerStaffReadOnly ||
            this.lockingStructureColumnName != null ||
            this.savingExternalMeta ||
            this.loadingOnSubmit
        ) {
            return;
        }
        const columnName = String(col?.name ?? '').trim();
        if (!columnName) return;

        const tableName = String(
            this.form.get('name')?.value ?? this.data1?.tableName ?? ''
        ).trim();
        const dataSourceId = this.data1?.dataSourceId;
        if (!tableName || dataSourceId == null || dataSourceId === '') {
            this.alert.failedMessage(
                this.transloco.translate('Missing table name or data source id')
            );
            return;
        }

        const nextLocked = !this.isStructureColumnLocked(col);
        if (nextLocked) {
            this.lockedStructureColumns.add(columnName);
            const needsUnlockBeforeSave =
                this.pendingUnlockStructureColumns.has(columnName);
            if (
                !this.initialLockedStructureColumns.has(columnName) ||
                needsUnlockBeforeSave
            ) {
                this.pendingLockStructureColumns.add(columnName);
            }
            const index = this.parsedStructure.findIndex(
                item => String(item?.name ?? '').trim() === columnName
            );
            if (index >= 0) {
                this.structureDescEditStates[index] = false;
            }
        } else {
            this.lockedStructureColumns.delete(columnName);
            this.pendingLockStructureColumns.delete(columnName);
            if (this.initialLockedStructureColumns.has(columnName)) {
                this.pendingUnlockStructureColumns.add(columnName);
            }
        }

        this.cdr.markForCheck();
    }

    private patchLockedColumnsInDialogData(): void {
        const nextLocked = Array.from(this.lockedStructureColumns.values());
        const patchHolder = (holder: any) => {
            if (!holder || typeof holder !== 'object') return;
            holder.lockedColumns = [...nextLocked];
            holder.locked_columns = [...nextLocked];
        };
        patchHolder(this.data1?.tableInfo);
        patchHolder(this.data1?.table);
        patchHolder(this.data1);
        if (this.data1?.tableInfo) {
            this.data1.tableInfo = { ...this.data1.tableInfo };
        }
    }

    private syncStructureControlFromParsed(): void {
        const ctrl = this.form.get('structure');
        if (!ctrl) return;

        const tableStructure = this.getTableStructureSection(
            this.structureJsonRoot
        );
        const metricStructure = tableStructure?.[
            'metric_columns_structure'
        ] as Record<string, unknown> | undefined;
        const dimensionStructure = tableStructure?.[
            'dimension_columns_structure'
        ] as Record<string, unknown> | undefined;

        if (metricStructure || dimensionStructure) {
            const updateSectionDescriptions = (
                section: Record<string, unknown> | undefined
            ): void => {
                if (!section || typeof section !== 'object') return;
                const keys = Array.isArray(section['K'])
                    ? (section['K'] as unknown[]).map(k => String(k))
                    : [];
                const descriptionIndex = keys.indexOf('description');
                if (descriptionIndex < 0) return;
                const colsObj = section['columns'] as
                    | Record<string, unknown>
                    | undefined;
                if (!colsObj || typeof colsObj !== 'object') return;

                for (const c of this.parsedStructure) {
                    const arr = colsObj[c.name];
                    if (
                        Array.isArray(arr) &&
                        arr.length > descriptionIndex
                    ) {
                        arr[descriptionIndex] = c.description ?? '';
                    }
                }
            };

            updateSectionDescriptions(dimensionStructure);
            updateSectionDescriptions(metricStructure);
            ctrl.setValue(JSON.stringify(this.structureJsonRoot));
        } else if (
            this.structureJsonRoot?.Columns &&
            typeof this.structureJsonRoot.Columns === 'object'
        ) {
            const colsObj = this.structureJsonRoot.Columns as Record<string, unknown>;
            for (const c of this.parsedStructure) {
                const arr = colsObj[c.name];
                if (Array.isArray(arr) && arr.length >= 2) arr[1] = c.description ?? '';
            }
            ctrl.setValue(JSON.stringify(this.structureJsonRoot));
        } else {
            ctrl.setValue(this.buildStructureTextFromParsed(this.parsedStructure));
        }

        this.structureTextViewValue = this.buildStructureTextFromParsed(
            this.parsedStructure
        );
    }

    private buildStructureTextFromParsed(cols: any[]): string {
        return cols
            .map((col) => {
                const lines: string[] = [];
                lines.push(`Column: ${col.name} (${col.type || ''})`);
                if (
                    col.semanticType === 'dimension' ||
                    col.semanticType === 'metric'
                ) {
                    lines.push(
                        `- Semantic Type: ${this.getSemanticTypePlainLabel(col.semanticType)}`
                    );
                }

                const dynamicKeys = this.structureColumnKeys?.length
                    ? this.structureColumnKeys.filter(key => key !== 'data_type')
                    : [];
                if (dynamicKeys.length) {
                    for (const key of dynamicKeys) {
                        const value = this.getStructureCellDisplay(col, key);
                        lines.push(
                            `- ${this.getStructureColumnLabel(key)}: ${value || '-'}`
                        );
                    }
                } else {
                    lines.push(`- Description: ${col.description ?? ''}`);
                    if (col.distinctValues != null && col.distinctValues !== '')
                        lines.push(`- Distinct Values: ${col.distinctValues}`);
                    if (col.nullCount != null && col.nullCount !== '')
                        lines.push(`- Null Count: ${col.nullCount}`);
                    if (col.sampleValues != null && col.sampleValues !== '')
                        lines.push(`- Sample Values: ${col.sampleValues}`);
                    if (col.minDate)
                        lines.push(`- Minimum Date: ${col.minDate}`);
                    if (col.maxDate)
                        lines.push(`- Maximum Date: ${col.maxDate}`);
                    if (col.unique != null && String(col.unique).trim() !== '')
                        lines.push(`- Unique: ${col.unique}`);
                }
                return lines.join('\n');
            })
            .join('\n\n');
    }

    getStructureTextViewDisplayValue(): string {
        const rawValue =
            this.form.get('structure')?.value ?? this.structureTextViewValue ?? '';

        if (rawValue == null) {
            return '';
        }

        if (typeof rawValue === 'object') {
            try {
                return JSON.stringify(rawValue, null, 2);
            } catch {
                return String(rawValue);
            }
        }

        const text = String(rawValue).trim();
        if (!text) {
            return '';
        }

        try {
            return JSON.stringify(JSON.parse(text), null, 2);
        } catch {
            return String(rawValue);
        }
    }

    copyStructureTextViewValue(): void {
        const value = this.getStructureTextViewDisplayValue();
        if (!value) {
            this.alert.failedMessage('Nothing to copy.');
            return;
        }

        const copied = this.clipboard.copy(value);
        if (copied) {
            this.alert.successMessage('Structure copied to clipboard.');
            return;
        }

        this.alert.failedMessage('Failed to copy structure.');
    }

    getSemanticTypeDisplay(col: any): string {
        if (col?.semanticType === 'dimension') {
            return this.transloco.translate('Dimension');
        }
        if (col?.semanticType === 'metric') {
            return this.transloco.translate('Metric');
        }
        return '';
    }

    private getSemanticTypePlainLabel(
        semanticType: 'dimension' | 'metric'
    ): string {
        return semanticType === 'dimension'
            ? this.transloco.translate('Dimension')
            : this.transloco.translate('Metric');
    }

    getStructureColumnLabel(columnKey: string): string {
        const labels: Record<string, string> = {
            data_type: 'Data Type',
            description: 'Description',
            distinct_count: 'Distinct Values',
            null_count: 'Null Count',
            sample_values: 'Sample Values',
            min_max: 'Min / Max',
            is_unique: 'Unique',
            aggregation_type: 'Aggregation Type',
            unit: 'Unit',
            unit_ref_column: 'Unit Ref Column',
        };
        return labels[columnKey] ?? columnKey.replace(/_/g, ' ');
    }

    getStructureCellDisplay(col: any, columnKey: string): string {
        const fromMap = col?.keyValueMap?.[columnKey];
        const rawValue =
            fromMap !== undefined ? fromMap : this.getStructureFallbackValue(col, columnKey);

        if (rawValue == null) {
            return '';
        }

        if (columnKey === 'distinct_count') {
            // Bounded columns hold only the LOWER bound in this slot, so the figure is a range.
            const label = describeDistinctCount(
                rawValue,
                col?.name,
                this.statisticsScope
            );
            return this.transloco.translate(label.key, label.params);
        }

        if (columnKey === 'is_unique') {
            return readIsUniqueFlag(rawValue) ? 'True' : 'False';
        }

        if (Array.isArray(rawValue)) {
            return rawValue.map(value => String(value ?? '').trim()).filter(Boolean).join(', ');
        }

        return String(rawValue).trim();
    }

    getStructureCellTooltip(col: any, columnKey: string): string {
        const fromMap = col?.keyValueMap?.[columnKey];
        const rawValue =
            fromMap !== undefined ? fromMap : this.getStructureFallbackValue(col, columnKey);

        if (columnKey === 'distinct_count') {
            return readDistinctValuesTooltip(rawValue);
        }

        if (Array.isArray(rawValue)) {
            return rawValue.map(value => String(value ?? '').trim()).filter(Boolean).join(', ');
        }

        return rawValue == null ? '' : String(rawValue).trim();
    }

    private getStructureFallbackValue(col: any, columnKey: string): unknown {
        switch (columnKey) {
            case 'data_type':
                return col?.type;
            case 'description':
                return col?.description;
            case 'distinct_count':
                return col?.distinctValues;
            case 'null_count':
                return col?.nullCount;
            case 'sample_values':
                return col?.sampleValues;
            case 'is_unique':
                return col?.unique;
            default:
                return '';
        }
    }

    // ---------------- utils ----------------
    private captureOriginalSchemaOnce(): void {
        if (
            !this.originalHeaders.length &&
            this.headers.length &&
            this.types.length
        ) {
            this.originalHeaders = [...this.headers];
            this.originalHeadersClean = this.originalHeaders.map(h =>
                this.fileService.cleanHeaderKey(h)
            );
            this.originalTypes = [...this.types];
            this.originalUnits =
                (this.form.get('units') as FormArray)?.value || [];
        }
    }

    private preparePayload(): UploadPayload {
        const columnTypesFA = this.form.get('columnTypes') as FormArray;
        const unitsFA = this.form.get('units') as FormArray;
        const { table, comments, dateColumns } = buildTableSchemaPayload(
            this.headers,
            this.headers.map((_, i) => (columnTypesFA.at(i)?.value as ColumnType) ?? this.types[i]),
            this.headers.map((_, i) => unitsFA.at(i)?.value),
            h => this.fileService.cleanHeaderKey(h)
        );

        return {
            name: this.form.get('name')!.value,
            description: this.form.get('description')!.value,
            dataSourceId: this.data1?.dataSourceId,
            table,
            comments,
            dateColumns,
            append: this.isAppending,
        };
    }

    private handleUploadError(error: any): void {
        const msg = (() => {
            try {
                return JSON.parse(error?.error)?.message;
            } catch {
                return error?.error?.message || 'Upload failed';
            }
        })();
        this.alert.failedMessage(msg, 6000);
    }

    private openInfoDialog(title: string, message: string) {
        this.dialog.open(MissingHeaderComponent, {
            width: '30%',
            panelClass: 'cropperDialog',
            data: { title, message },
        });
    }

    private scrollToHeader(index: number): void {
        const el = document.getElementById(`header-${index}`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', inline: 'center' });
            el.classList.add('highlight');
            setTimeout(() => el.classList.remove('highlight'), 1600);
        }
    }

    private get kpiAddInternalTableId(): number | string | null {
        const tableInfo = this.data1?.tableInfo;
        return (
            tableInfo?.id ??
            tableInfo?.tableId ??
            tableInfo?.eid ??
            null
        );
    }

    private get kpiAddExternalTableId(): number | string | null {
        const tableInfo = this.data1?.tableInfo;
        // External tables are returned as `id`/`value` pairs.
        return (
            tableInfo?.id ??
            tableInfo?.externalTableId ??
            tableInfo?.eid ??
            null
        );
    }

    get isKpiAddDisabled(): boolean {
        if (this.isForeignReadOnlyTable) {
            return true;
        }
        const isExternal = this.isExternalTableDialog();
        return isExternal
            ? this.kpiAddExternalTableId == null
            : this.kpiAddInternalTableId == null;
    }

    get isRegenerateKpisDisabled(): boolean {
        return this.isKpiAddDisabled || this.isRegeneratingKpis;
    }

    /**
     * Add flows pass the file `event` from the picker; view flows pass `event: null`.
     * KPI / Core Logic only applies when viewing or editing an existing table context.
     */
    get showCoreLogicSection(): boolean {
        const tableTypeRaw =
            this.tableTypeDisplay ||
            this.data1?.tableInfo?.table_type ||
            this.data1?.tableInfo?.tableType ||
            this.data1?.table?.table_type ||
            this.data1?.table?.tableType;
        const isDimensionTable =
            String(tableTypeRaw ?? '').trim().toLowerCase() === 'dimension';

        return !this.data1?.event && !this.data1?.files?.length && !isDimensionTable;
    }

    get columnDataQualityFlagEntries(): ColumnDataQualityFlagEntry[] {
        const fromStructure = this.extractColumnDataQualityFlagsFromText(
            String(this.form?.get('structure')?.value ?? '').trim()
        );
        if (fromStructure.length) {
            return fromStructure;
        }
        const candidateSources: unknown[] = [
            this.data1?.tableInfo?.column_data_quality_flags,
            this.data1?.tableInfo?.columnDataQualityFlags,
            this.data1?.table?.column_data_quality_flags,
            this.data1?.table?.columnDataQualityFlags,
            this.data1?.column_data_quality_flags,
            this.data1?.columnDataQualityFlags,
            this.data1?.data?.column_data_quality_flags,
            this.data1?.data?.columnDataQualityFlags,
            this.data1?.response?.data?.column_data_quality_flags,
            this.data1?.response?.data?.columnDataQualityFlags,
        ];

        for (const source of candidateSources) {
            const entries = this.extractColumnDataQualityFlagsFromObject(source);
            if (entries.length) {
                return entries;
            }
        }
        return [];
    }

    get showColumnDataQualityFlagsSection(): boolean {
        return this.columnDataQualityFlagEntries.length > 0;
    }

    async regenerateKpis(): Promise<void> {
        if (this.isForeignReadOnlyTable) {
            return;
        }
        const isExternal = this.isExternalTableDialog();
        const tableId = isExternal ? null : this.kpiAddInternalTableId;
        const externalTableId = isExternal ? this.kpiAddExternalTableId : null;

        if (isExternal && externalTableId == null) {
            this.alert.failedMessage('External table id is missing.');
            return;
        }
        if (!isExternal && tableId == null) {
            this.alert.failedMessage('Table id is missing.');
            return;
        }

        const confirmation = this.confirmationService.open({
            title: this.transloco.translate('Regenerate KPIs'),
            message: this.transloco.translate(
                'Are you sure you want to regenerate KPIs for this table? This will replace the current KPI list.'
            ),
            actions: {
                confirm: {
                    label: this.transloco.translate('Regenerate'),
                    color: 'warn',
                },
                cancel: {
                    label: this.transloco.translate('Cancel'),
                },
            },
        });

        const result = await firstValueFrom(confirmation.afterClosed());
        if (result !== 'confirmed') {
            return;
        }

        const payload = {
            isExternal: !!isExternal,
            tableId: isExternal ? 0 : Number(tableId),
            externalTableId: isExternal ? Number(externalTableId) : 0,
        };

        this.isRegeneratingKpis = true;
        this.cdr.markForCheck();

        try {
            const res = await firstValueFrom(
                this.http.post(
                    `${environment.apiLink}kpi-logic/regenerate-kpis`,
                    payload
                )
            );

            const kpis = this.extractKpisFromRegenerateResponse(res);
            if (!kpis?.length) {
                this.alert.failedMessage(
                    'Regenerated KPI data was not found in API response.'
                );
                return;
            }

            this.metricDefinitions = kpis;

            const tableInfo = this.data1?.tableInfo as any;
            if (tableInfo) {
                const targetKey = isExternal
                    ? Array.isArray(tableInfo.kpis)
                        ? 'kpis'
                        : Array.isArray(tableInfo.kpiLogic)
                          ? 'kpiLogic'
                          : 'kpis'
                    : Array.isArray(tableInfo.kpiLogic)
                      ? 'kpiLogic'
                      : Array.isArray(tableInfo.kpis)
                        ? 'kpis'
                        : 'kpiLogic';

                tableInfo[targetKey] = [...kpis];
                this.data1.tableInfo = { ...tableInfo };
            }

            this.alert.successMessage('KPIs regenerated successfully');
            this.cdr.markForCheck();
        } catch (e: any) {
            this.alert.failedMessage(this.getRegenerateKpisErrorMessage(e));
        } finally {
            this.isRegeneratingKpis = false;
            this.cdr.markForCheck();
        }
    }

    private extractKpisFromRegenerateResponse(
        response: any
    ): KpiLogicItem[] | null {
        const mapItem = (item: any): KpiLogicItem | null => {
            if (!item || typeof item !== 'object') return null;

            const id = item?.id ?? item?.kpi_id ?? item?.kpiId ?? undefined;
            const level =
                item?.level ??
                item?.kpi_level ??
                item?.kpiLevel ??
                undefined;

            const kpi_name =
                item?.kpi_name ?? item?.kpiName ?? item?.name ?? '';

            const calculation =
                item?.calculation ??
                item?.formula ??
                item?.expression ??
                '';

            const description =
                item?.description ?? item?.desc ?? item?.about ?? '';

            if (
                (kpi_name ?? '') === '' &&
                (calculation ?? '') === '' &&
                (description ?? '') === ''
            ) {
                return null;
            }

            const columns_used = Array.isArray(item?.columns_used)
                ? item.columns_used.map(String)
                : undefined;

            const isLocked = Boolean(
                item?.isLocked ?? item?.locked ?? item?.is_locked
            );

            return {
                id,
                level,
                kpi_name: String(kpi_name),
                calculation: String(calculation),
                description: String(description),
                isLocked,
                ...(columns_used?.length ? { columns_used } : {}),
            };
        };

        const collected: any[] = [];
        const collect = (v: any) => {
            if (Array.isArray(v)) collected.push(...v);
            else if (v && typeof v === 'object') collected.push(v);
        };

        collect(response?.data?.kpi);
        collect(response?.data?.kpis);
        collect(response?.data?.kpiLogic);
        collect(response?.data?.kpi_logic);
        collect(response?.data?.kpiLogicItems);
        collect(response?.data?.coreLogic);
        collect(response?.data?.core_logic);

        collect(response?.data?.data);
        collect(response?.data?.data?.kpi);
        collect(response?.data?.data?.kpis);
        collect(response?.data?.data?.kpiLogic);
        collect(response?.data?.data?.coreLogic);

        collect(response?.kpi);
        collect(response?.kpis);

        if (collected.length === 0) return null;

        const mapped = collected
            .map(item => mapItem(item))
            .filter((v): v is KpiLogicItem => !!v);

        return mapped.length ? mapped : null;
    }

    private extractColumnDataQualityFlagsFromText(
        structureText: string
    ): ColumnDataQualityFlagEntry[] {
        if (!structureText) {
            return [];
        }
        try {
            const parsed = JSON.parse(structureText) as Record<string, unknown>;
            const direct = this.extractColumnDataQualityFlagsFromObject(
                parsed['column_data_quality_flags']
            );
            if (direct.length) {
                return direct;
            }

            const nested = parsed['table_structure'];
            if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
                return this.extractColumnDataQualityFlagsFromObject(
                    (nested as Record<string, unknown>)[
                        'column_data_quality_flags'
                    ]
                );
            }
        } catch {
            // Non-JSON structure text.
        }
        return [];
    }

    private extractColumnDataQualityFlagsFromObject(
        source: unknown
    ): ColumnDataQualityFlagEntry[] {
        if (!source || typeof source !== 'object' || Array.isArray(source)) {
            return [];
        }

        return Object.entries(source as Record<string, unknown>)
            .map(([columnName, rawFlags]) => {
                const flags = Array.isArray(rawFlags)
                    ? rawFlags
                          .map(flag => String(flag ?? '').trim())
                          .filter(Boolean)
                    : [];
                return { columnName, flags };
            })
            .filter(entry => entry.flags.length > 0);
    }

    private getRegenerateKpisErrorMessage(error: any): string {
        try {
            const msg = error?.error ?? error?.message;
            if (typeof msg === 'string') {
                return JSON.parse(msg)?.message ?? msg;
            }
            return error?.error?.message ?? error?.message ?? 'Request failed';
        } catch {
            return error?.error?.message ?? error?.message ?? 'Request failed';
        }
    }

    openAddKpiDialog(): void {
        if (this.isForeignReadOnlyTable) {
            return;
        }
        const isExternal = this.isExternalTableDialog();
        const tableId = isExternal ? null : this.kpiAddInternalTableId;
        const externalTableId = isExternal ? this.kpiAddExternalTableId : null;

        if (isExternal && externalTableId == null) {
            this.alert.failedMessage('External table id is missing.');
            return;
        }
        if (!isExternal && tableId == null) {
            this.alert.failedMessage('Table id is missing.');
            return;
        }

        const sourceRows =
            this.allRows.length > 0 ? this.allRows : this.rows;
        const previewRows = sourceRows.slice(
            0,
            FORMULA_DIALOG_TABLE_PREVIEW_MAX_ROWS
        );

        const dialogRef = this.dialog.open(AddKpiPromptDialogComponent, {
            width: 'min(96vw, 1400px)',
            maxWidth: '100vw',
            maxHeight: '95dvh',
            panelClass: 'biggDialog',
            autoFocus: false,
            disableClose: true,
            data: {
                isExternal,
                tableId: tableId ?? undefined,
                externalTableId: externalTableId ?? undefined,
                lang: this.lang,
                previewHeaders: [...this.headers],
                previewRows,
            },
        });

        dialogRef.afterClosed().subscribe((newKpis: KpiLogicItem[] | undefined) => {
            if (!newKpis?.length) return;

            this.metricDefinitions = this.upsertKpiLogicList(
                this.metricDefinitions,
                newKpis
            );

            // Keep underlying data in sync so future edits use the latest values.
            const tableInfo = this.data1?.tableInfo as any;
            if (tableInfo) {
                const targetKey = isExternal
                    ? Array.isArray(tableInfo.kpis)
                        ? 'kpis'
                        : Array.isArray(tableInfo.kpiLogic)
                          ? 'kpiLogic'
                          : 'kpis'
                    : Array.isArray(tableInfo.kpiLogic)
                      ? 'kpiLogic'
                      : Array.isArray(tableInfo.kpis)
                        ? 'kpis'
                        : 'kpiLogic';

                if (!Array.isArray(tableInfo[targetKey])) tableInfo[targetKey] = [];

                const existing = tableInfo[targetKey] as KpiLogicItem[];
                tableInfo[targetKey] = this.upsertKpiLogicList(existing, newKpis);
                this.data1.tableInfo = { ...tableInfo };
            }

            this.alert.successMessage('KPI added successfully');
            this.cdr.markForCheck();
        });
    }

    private upsertKpiLogicList(
        existing: KpiLogicItem[],
        incoming: KpiLogicItem[]
    ): KpiLogicItem[] {
        let list = [...existing];
        const inserts: KpiLogicItem[] = [];

        for (const item of incoming) {
            const matchIdx = list.findIndex(
                m =>
                    (item.id != null &&
                        m.id != null &&
                        String(m.id) === String(item.id)) ||
                    (item.kpi_name &&
                        String(m.kpi_name) === String(item.kpi_name))
            );
            if (matchIdx !== -1) {
                list = list.map((m, i) =>
                    i === matchIdx ? { ...m, ...item } : m
                );
            } else {
                inserts.push(item);
            }
        }
        // Newly added KPIs appear first (most recent on top).
        return [...inserts, ...list];
    }

    async confirmDeleteKpi(index: number): Promise<void> {
        if (this.isForeignReadOnlyTable) {
            return;
        }
        const metric = this.metricDefinitions[index];
        if (!metric) return;
        if (metric.id == null) {
            this.alert.failedMessage('KPI id missing for this metric.');
            return;
        }
        if (metric.isLocked) {
            return;
        }
        if (this.isDeletingKpi || this.isRegeneratingKpis) return;

        const confirmation = this.confirmationService.open({
            title: this.transloco.translate('Delete KPI'),
            message: this.transloco.translate(
                'Are you sure you want to delete this KPI? This cannot be undone.'
            ),
            actions: {
                confirm: {
                    label: this.transloco.translate('Delete'),
                    color: 'warn',
                },
                cancel: {
                    label: this.transloco.translate('Cancel'),
                },
            },
        });

        const result = await firstValueFrom(confirmation.afterClosed());
        if (result !== 'confirmed') return;

        this.isDeletingKpi = true;
        this.cdr.markForCheck();

        try {
            await firstValueFrom(this.kpiLogicService.deleteKpi(metric.id));
            this.removeKpiFromDialogData(metric.id);
            this.alert.successMessage(
                this.transloco.translate('KPI deleted successfully')
            );
        } catch (e: any) {
            this.alert.failedMessage(
                e?.error?.message ?? 'Failed to delete KPI.'
            );
        } finally {
            this.isDeletingKpi = false;
            this.cdr.markForCheck();
        }
    }

    isKpiLocking(metric: KpiLogicItem): boolean {
        return (
            metric.id != null &&
            this.lockingKpiId != null &&
            String(metric.id) === this.lockingKpiId
        );
    }

    async toggleKpiLock(metric: KpiLogicItem, index: number): Promise<void> {
        if (this.isForeignReadOnlyTable) {
            return;
        }
        if (metric.id == null) {
            this.alert.failedMessage(
                this.transloco.translate('KPI id missing for this metric.')
            );
            return;
        }
        if (
            this.isRegeneratingKpis ||
            this.isDeletingKpi ||
            this.lockingKpiId != null
        ) {
            return;
        }

        const kpiIdNum = Number(metric.id);
        if (!Number.isFinite(kpiIdNum)) {
            this.alert.failedMessage('Invalid KPI id.');
            return;
        }

        const nextLocked = !metric.isLocked;
        this.lockingKpiId = String(metric.id);
        this.cdr.markForCheck();

        try {
            await firstValueFrom(
                this.kpiLogicService.setKpiLock(kpiIdNum, nextLocked)
            );
            this.metricDefinitions = this.metricDefinitions.map((m, i) =>
                i === index ? { ...m, isLocked: nextLocked } : m
            );
            this.patchKpiLockInDialogData(metric.id, nextLocked);
        } catch (e: any) {
            this.alert.failedMessage(
                e?.error?.message ?? 'Failed to update KPI lock.'
            );
        } finally {
            this.lockingKpiId = null;
            this.cdr.markForCheck();
        }
    }

    private patchKpiLockInDialogData(
        kpiId: number | string,
        isLocked: boolean
    ): void {
        const idStr = String(kpiId);
        const patchHolder = (holder: any) => {
            if (!holder) return;
            for (const key of ['kpiLogic', 'kpis'] as const) {
                const arr = holder[key];
                if (!Array.isArray(arr)) continue;
                holder[key] = arr.map((item: any) => {
                    const id = item?.id ?? item?.kpi_id ?? item?.kpiId;
                    if (id != null && String(id) === idStr) {
                        return { ...item, isLocked, locked: isLocked };
                    }
                    return item;
                });
            }
        };
        patchHolder(this.data1?.tableInfo);
        patchHolder(this.data1?.table);
        patchHolder(this.data1);
        if (this.data1?.tableInfo) {
            this.data1.tableInfo = { ...this.data1.tableInfo };
        }
    }

    private removeKpiFromDialogData(kpiId: number | string): void {
        const idStr = String(kpiId);
        const matchesRemoved = (item: any) =>
            item?.id != null &&
            String(item.id ?? item.kpi_id ?? item.kpiId) === idStr;

        this.metricDefinitions = this.metricDefinitions.filter(
            m => m.id == null || String(m.id) !== idStr
        );

        const patch = (holder: any) => {
            if (!holder) return;
            for (const key of ['kpiLogic', 'kpis'] as const) {
                const arr = holder[key];
                if (Array.isArray(arr)) {
                    holder[key] = arr.filter(item => !matchesRemoved(item));
                }
            }
        };

        patch(this.data1?.tableInfo);
        patch(this.data1?.table);
        patch(this.data1);
    }

    openFormulaPromptDialog(index: number): void {
        if (this.isForeignReadOnlyTable) {
            return;
        }
        const metric = this.metricDefinitions[index];
        if (!metric) return;
        if (metric.id == null) {
            this.alert.failedMessage('KPI id missing for this metric.');
            return;
        }
        const sourceRows =
            this.allRows.length > 0 ? this.allRows : this.rows;
        const isMobile = window.innerWidth <= 767;
        const maxRows = isMobile ? 30 : FORMULA_DIALOG_TABLE_PREVIEW_MAX_ROWS;
        const previewRows = sourceRows.slice(0, maxRows);

        const dialogRef = this.dialog.open(FormulaPromptDialogComponent, {
            width: 'min(96vw, 1400px)',
            maxHeight: '95dvh',
            panelClass: 'biggDialog',
            autoFocus: false,
            disableClose: true,
            data: {
                metricName: metric.kpi_name,
                kpiId: metric.id,
                isExternal: this.isExternalTableDialog(),
                tableId: this.isExternalTableDialog()
                    ? undefined
                    : (this.kpiAddInternalTableId ??
                        this.data1?.tableInfo?.id ??
                        this.data1?.tableInfo?.tableId ??
                        this.data1?.table?.id ??
                        this.data1?.tableId ??
                        undefined),
                externalTableId: this.isExternalTableDialog()
                    ? (this.kpiAddExternalTableId ??
                        this.data1?.tableInfo?.id ??
                        this.data1?.externalTableId ??
                        undefined)
                    : undefined,
                currentLevel: metric.level,
                currentCalculation: metric.calculation,
                currentDescription: metric.description,
                currentColumnsUsed: metric.columns_used ?? [],
                lang: this.lang,
                previewHeaders: [...this.headers],
                previewRows,
            },
        });
        dialogRef.afterClosed().subscribe((updated: KpiLogicItem[] | undefined) => {
            if (!updated) return;
            if (updated.length === 1) {
                const updatedItem = updated[0];

                const matchIdx = this.metricDefinitions.findIndex(
                    m =>
                        (updatedItem.id != null &&
                            m.id != null &&
                            String(m.id) === String(updatedItem.id)) ||
                        (String(m.kpi_name) ===
                            String(updatedItem.kpi_name))
                );

                if (matchIdx !== -1) {
                    this.metricDefinitions = this.metricDefinitions.map(
                        (m, i) => (i === matchIdx ? { ...m, ...updatedItem } : m)
                    );
                } else {
                    this.metricDefinitions = [
                        ...this.metricDefinitions,
                        updatedItem,
                    ];
                }
                // Keep underlying object in sync (merge, don't replace entire list).
                if (this.data1?.tableInfo?.kpiLogic != null) {
                    this.data1.tableInfo.kpiLogic = this.mergeKpiLogic(
                        this.data1.tableInfo.kpiLogic,
                        updated
                    );
                } else if (this.data1?.table?.kpiLogic != null) {
                    this.data1.table.kpiLogic = this.mergeKpiLogic(
                        this.data1.table.kpiLogic,
                        updated
                    );
                } else {
                    this.data1.kpiLogic = this.mergeKpiLogic(
                        this.data1.kpiLogic,
                        updated
                    );
                }
            } else {
                // If API returns a full list, replace completely.
                this.metricDefinitions = updated;
                if (this.data1?.tableInfo?.kpiLogic != null)
                    this.data1.tableInfo.kpiLogic = updated;
                else if (this.data1?.table?.kpiLogic != null)
                    this.data1.table.kpiLogic = updated;
                else this.data1.kpiLogic = updated;
            }
            this.alert.successMessage('Prompt submitted successfully');
            this.cdr.markForCheck();
        });
    }

    private mergeKpiLogic(
        existing: KpiLogicItem[] | undefined,
        updated: KpiLogicItem[]
    ): KpiLogicItem[] {
        const list = Array.isArray(existing) ? existing : [];
        if (!updated.length) return list;

        const updatedItem = updated[0];
        const matchIdx = list.findIndex(
            m =>
                (updatedItem.id != null &&
                    m.id != null &&
                    String(m.id) === String(updatedItem.id)) ||
                String(m.kpi_name) === String(updatedItem.kpi_name)
        );

        if (matchIdx === -1) return [...list, ...updated];
        return list.map((m, i) => (i === matchIdx ? { ...m, ...updatedItem } : m));
    }

    validateTableName(ctrl: AbstractControl) {
        const maxLen = 63;
        const v = ctrl.value;
        if (typeof v !== 'string') return { invalidType: true };
        if (v.length > maxLen) return { maxLengthExceeded: true };
        if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(v)) return { invalidName: true };
        return null;
    }

    generateTableNameFromFile(fileName: string): string {
        return toTableNameSlug(fileName.split('.').slice(0, -1).join('.'));
    }

    get isDateTypeMissingUnit(): boolean {
        const columnTypes = this.form.get('columnTypes') as FormArray;
        const units = this.form.get('units') as FormArray;
        for (let i = 0; i < columnTypes.length; i++) {
            if (columnTypes.at(i).value === 'DATE' && !units.at(i).value)
                return true;
        }
        return false;
    }

    formatTablePreviewCell(header: string, value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }
        if (!/^year$/i.test(String(header).trim())) {
            return String(value);
        }
        if (typeof value === 'number' && Number.isFinite(value)) {
            return Math.trunc(value).toLocaleString('en-US', {
                useGrouping: false,
                maximumFractionDigits: 0,
            });
        }
        if (typeof value === 'bigint') {
            return Number(value).toLocaleString('en-US', {
                useGrouping: false,
                maximumFractionDigits: 0,
            });
        }
        if (typeof value === 'string') {
            const cleaned = value.replace(/,/g, '').trim();
            if (cleaned === '') {
                return '';
            }
            const n = Number(cleaned);
            if (
                Number.isFinite(n) &&
                /^-?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?$/.test(cleaned)
            ) {
                return Math.trunc(n).toLocaleString('en-US', {
                    useGrouping: false,
                    maximumFractionDigits: 0,
                });
            }
            return value;
        }
        return String(value);
    }

    exportCoreLogicAsCsv(): void {
        if (!Array.isArray(this.metricDefinitions) || !this.metricDefinitions.length) {
            this.alert.failedMessage('No core logic data available');
            return;
        }

        const header = [
            'Level',
            'KPI Name',
            'Calculation',
            'Description',
        ];
        const rows = this.metricDefinitions.map(metric => [
            metric.level ?? '',
            metric.kpi_name ?? '',
            metric.calculation ?? '',
            metric.description ?? '',
        ]);
        const csv = [header, ...rows]
            .map(row => row.map(cell => this.escapeCsvValue(cell)).join(','))
            .join('\n');

        const blob = new Blob(['\uFEFF' + csv], {
            type: 'text/csv;charset=utf-8;',
        });
        const tableName = this.toSafeFileName(
            this.form.get('name')?.value || this.data1?.tableName || 'table'
        );
        const fileName = `ds_${tableName}_core_logic.csv`;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    private escapeCsvValue(value: unknown): string {
        const normalized = value == null ? '' : String(value);
        const escaped = normalized.replace(/"/g, '""');
        return /[",\r\n]/.test(escaped) ? `"${escaped}"` : escaped;
    }

    private toSafeFileName(name: string): string {
        const value = String(name ?? '')
            .trim()
            .replace(/[<>:"/\\|?*]+/g, '_')
            .replace(/\s+/g, '_')
            .replace(/_+/g, '_')
            .replace(/^_+|_+$/g, '');
        return value || 'table';
    }

    trackByHeader = (_: number, h: string) => h;
    trackBySheetDraft = (_: number, d: SheetDraft) => d.key;
    readonly sheetTabTitle = sheetDraftTitle;
    trackByRow = (i: number, _row: any) => i;
    trackByColumnStructure = (i: number, col: any) =>
        `${i}_${col?.semanticType ?? ''}_${col?.name ?? ''}`;

    get columnTypesFA(): FormArray {
        return this.form.get('columnTypes') as FormArray;
    }
    get unitsFA(): FormArray {
        return this.form.get('units') as FormArray;
    }

    /**
     * View / server pages hide the system primary-key `id` column.
     * Add / Append keep an Excel/CSV `id` header so submit can treat it like other
     * reserved names (`date`, etc.) and prompt the user to rename before save.
     */
    private sanitizeHeaders(headers: string[]): string[] {
        if (this.data1?.type === 'Add' || this.isAppending) {
            return headers;
        }
        return headers.filter(h => String(h).trim().toLowerCase() !== 'id');
    }
}
